import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Switch, FormControlLabel, Menu, MenuItem, Pagination, FormControl, InputLabel, Select, Chip, CircularProgress
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ApprovalIcon from '@mui/icons-material/Approval';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import CampaignIcon from '@mui/icons-material/Campaign';
import ReporterList from './ReporterList';
import Article from './Article';
import EditArticle from './EditArticle';
import DashboardLayout from '../components/DashboardLayout';
import CustomPopup from '../components/CustomPopup';
import SuperAdminRoutes from './SuperAdminRoutes';
import { setPopupCallback, showSuccessPopup, showErrorPopup, showWarningPopup } from '../utils/popupManager';

// Utility function to capitalize first letter of each word
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Dashboard Home Component
function DashboardHome() {
  return (
    <Box sx={{ 
      p: 4, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: '#fafbfc'
    }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          color: '#1976d2', 
          fontWeight: 700,
          fontSize: '2.5rem',
          mb: 1
        }}>
          Super Admin Dashboard
        </Typography>
        <Typography variant="h6" sx={{ 
          color: '#666', 
          fontWeight: 400,
          fontSize: '1.1rem'
        }}>
          Welcome to your comprehensive admin control panel
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Grid container spacing={4}>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: '#e3f2fd',
            borderRadius: 3,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(25, 118, 210, 0.15)'
            }
          }}>
            <Typography variant="h3" color="primary" gutterBottom sx={{ 
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 2
            }}>
              24
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ 
              fontWeight: 500,
              fontSize: '1.1rem'
            }}>
              Total Articles
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: '#f3e5f5',
            borderRadius: 3,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(156, 39, 176, 0.15)'
            }
          }}>
            <Typography variant="h3" color="secondary" gutterBottom sx={{ 
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 2
            }}>
              8
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ 
              fontWeight: 500,
              fontSize: '1.1rem'
            }}>
              Categories
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: '#e8f5e8',
            borderRadius: 3,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)'
            }
          }}>
            <Typography variant="h3" color="success.main" gutterBottom sx={{ 
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 2
            }}>
              12
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ 
              fontWeight: 500,
              fontSize: '1.1rem'
            }}>
              Active Users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: '#fff3e0',
            borderRadius: 3,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)'
            }
          }}>
            <Typography variant="h3" color="warning.main" gutterBottom sx={{ 
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 2
            }}>
              156
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ 
              fontWeight: 500,
              fontSize: '1.1rem'
            }}>
              Total Views
            </Typography>
          </Paper>
        </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

// Users View Component
function UsersView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', phone: '', email: '', roleType: 1, emailotp: '' });
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  const users = [];

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.role.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEdit = () => {
    console.log('Edit user:', selectedUser);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete user:', selectedUser);
    handleMenuClose();
  };

  // Validation functions
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    if (!phone.trim()) {
      setPhoneError('');
      return true;
    }
    if (!phoneRegex.test(phone.trim())) {
      setPhoneError('Phone number must be exactly 10 digits');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!email.trim()) {
      setEmailError('');
      return true;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError('Email must be a valid Gmail address (e.g., example@gmail.com)');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleVerifyEmail = async () => {
    if (!newUser.email.trim()) {
      showWarningPopup('Please enter an email address first', 'Email Required');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newUser.email.trim())) {
      showWarningPopup('Please enter a valid Gmail address (e.g., example@gmail.com)', 'Invalid Email Format');
      return;
    }

    setVerifyingEmail(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/send_email_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newUser.email }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Email verification initiated:', result);
        setEmailVerified(true);
        showSuccessPopup('OTP sent to your email. Please check and enter the OTP.', 'Email Verification');
      } else {
        const errorData = await response.json();
        showErrorPopup(`Error: ${errorData.message || 'Failed to send OTP'}`, 'OTP Error');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      showErrorPopup('Error sending OTP. Please try again.', 'Network Error');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.phone.trim()) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    // Validate phone number - must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(newUser.phone.trim())) {
      showWarningPopup('Please enter a valid 10-digit phone number', 'Invalid Phone Number');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newUser.email.trim())) {
      showWarningPopup('Please enter a valid Gmail address (e.g., example@gmail.com)', 'Invalid Email Format');
      return;
    }

    if (!emailVerified || !newUser.emailotp.trim()) {
      showWarningPopup('Please verify your email with OTP first', 'Email Verification Required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/create_dashboard_admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('User added successfully:', result);
        setNewUser({ name: '', phone: '', email: '', roleType: 1, emailotp: '' });
        setEmailVerified(false);
        setAddUserOpen(false);
        // You might want to refresh the users list here
      } else {
        const errorData = await response.json();
        showErrorPopup(`Error: ${errorData.message || 'Failed to add user'}`, 'Add User Error');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      showErrorPopup('Error adding user. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: 4, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: '#fafbfc'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        pb: 2,
        borderBottom: '2px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            color: '#1976d2', 
            fontWeight: 700,
            fontSize: '2.2rem',
            mb: 1
          }}>
            Users Management
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666', 
            fontWeight: 400,
            fontSize: '1rem'
          }}>
            Manage user accounts and permissions
          </Typography>
        </Box>
                 <Button 
           variant="contained" 
           onClick={() => setAddUserOpen(true)}
           sx={{ bgcolor: '#1976d2' }}
         >
           + Add User
         </Button>
      </Box>
      
      {/* Search Controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
               <TableCell>Role</TableCell>
               <TableCell>Email</TableCell>
               <TableCell>Status</TableCell>
               {/* <TableCell>Last Login</TableCell> */}
               <TableCell>Actions</TableCell>
             </TableRow>
           </TableHead>
          <TableBody>
            {filteredUsers.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{user.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: user.role === 'Super Admin' ? '#d32f2f' : 
                             user.role === 'Quality Manager' ? '#1976d2' : '#388e3c',
                      fontWeight: 500 
                    }}
                  >
                    {user.role}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                </TableCell>
                                 <TableCell>
                   <Typography 
                     color={user.status === 'Active' ? 'success.main' : 'error.main'}
                     sx={{ fontWeight: 500 }}
                   >
                     {user.status}
                   </Typography>
                 </TableCell>
                 {/* <TableCell>
                   <Typography variant="body2" color="text.secondary">
                     {user.lastLogin}
                   </Typography>
                 </TableCell> */}
                 <TableCell>
                  <IconButton
                    size="small"
                    onClick={(event) => handleMenuOpen(event, user)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Box>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedUser && selectedUser.status === 'Active' ? (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newUser.name}
            onChange={(e) => setNewUser({...newUser, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter full name..."
            autoFocus
          />
                     <TextField
             label="Phone"
             fullWidth
             margin="normal"
             value={newUser.phone}
             onChange={(e) => {
               setNewUser({...newUser, phone: e.target.value});
               validatePhone(e.target.value);
             }}
             onBlur={(e) => validatePhone(e.target.value)}
             placeholder="Enter phone number..."
             error={!!phoneError}
             helperText={phoneError}
           />
                     <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                           <TextField
                label="Email"
                fullWidth
                margin="normal"
                value={newUser.email}
                onChange={(e) => {
                  setNewUser({...newUser, email: e.target.value});
                  validateEmail(e.target.value);
                }}
                onBlur={(e) => validateEmail(e.target.value)}
                placeholder="Enter email address..."
                type="email"
                error={!!emailError}
                helperText={emailError}
              />
             <Button
               variant="outlined"
               onClick={handleVerifyEmail}
               disabled={verifyingEmail || !newUser.email.trim()}
               sx={{ mb: 1, minWidth: 100 }}
             >
               {verifyingEmail ? 'Sending...' : 'Verify'}
             </Button>
           </Box>
           <TextField
             select
             label="Role Type"
             fullWidth
             margin="normal"
             value={newUser.roleType}
             onChange={(e) => setNewUser({...newUser, roleType: parseInt(e.target.value)})}
           >
             <MenuItem value={1}>Super Admin</MenuItem>
             <MenuItem value={2}>Quality Manager</MenuItem>
             <MenuItem value={3}>Reporter</MenuItem>
           </TextField>
           {emailVerified && (
             <TextField
               label="Email OTP"
               fullWidth
               margin="normal"
               value={newUser.emailotp}
               onChange={(e) => setNewUser({...newUser, emailotp: e.target.value})}
               placeholder="Enter email OTP..."
             />
           )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
                     <Button 
             onClick={handleAddUser} 
             variant="contained"
             disabled={loading || !newUser.name.trim() || !newUser.email.trim() || !newUser.phone.trim() || !emailVerified || !newUser.emailotp.trim()}
             sx={{ bgcolor: '#1976d2' }}
           >
             {loading ? 'Adding...' : 'Add User'}
           </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Categories View Component
function CategoriesView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', imageId: '', imageUrl: '', sNo: 1 });
  const [editCategory, setEditCategory] = useState({ categoryId: '', name: '', imageId: '', imageUrl: '', sNo: 1 });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [editSelectedFile, setEditSelectedFile] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Categories:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch categories data
  const fetchCategories = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for categories');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          isActiveFilter: true, // Always filter by active status
          isActive: activeToggle, // Show active categories when toggle is on, inactive when toggle is off
          isSearchFilter: searchTerm.trim() !== "",
          search: searchTerm.trim(),
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Categories fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setCategories(result.data);
          const total = result.totalCount || result.data.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setCategories([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching categories:', errorData);
        setCategories([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, searchTerm]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeToggle]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Use categories directly since filtering is now done server-side, but sort by sNo as fallback
  const filteredCategories = categories.sort((a, b) => {
    const aSNo = a.sNo || 999; // Default to high number if sNo is missing
    const bSNo = b.sNo || 999;
    return aSNo - bSNo; // Sort in ascending order
  });

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Handle edit file selection
  const handleEditFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setEditSelectedFile(file);
      const url = URL.createObjectURL(file);
      setEditPreviewUrl(url);
    }
  };

  // Upload image function
  const uploadImage = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading image with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    const response = await fetch('https://api.jpnewz.com/upload/upload_image', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    console.log('📥 Upload image response:', result);

    if (!response.ok || !result.status) {
      throw new Error(result.message || 'Failed to upload image');
    }

    // Check if result.data exists and has the expected structure
    if (!result.data) {
      console.error('Upload response missing data:', result);
      throw new Error('Invalid response from upload server');
    }

    // Validate the response structure
    if (!result.data._id || !result.data.s3FileURL) {
      console.error('Upload response missing required fields:', result.data);
      throw new Error('Upload response missing required fields (_id or s3FileURL)');
    }

    return result.data;
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      showWarningPopup('Please enter a category name', 'Category Name Required');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      let imageId = '';
      let imageUrl = '';

      // Upload image if selected
      if (selectedFile) {
        setUploadingImage(true);
        try {
          console.log('🔄 Starting image upload for file:', selectedFile.name);
          const uploadResult = await uploadImage(selectedFile);
          
          // Validate upload result
          if (!uploadResult || !uploadResult._id) {
            throw new Error('Upload result is missing _id field');
          }
          
          imageId = uploadResult._id;
          imageUrl = uploadResult.s3FileURL || '';
          
          console.log('✅ Image uploaded successfully:', {
            imageId,
            imageUrl,
            fullResult: uploadResult
          });
        } catch (error) {
          console.error('❌ Error uploading image:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            selectedFile: selectedFile ? {
              name: selectedFile.name,
              size: selectedFile.size,
              type: selectedFile.type
            } : 'No file selected'
          });
          showErrorPopup(`Error uploading image: ${error.message}`, 'Upload Error');
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        sNo: newCategory.sNo,
        name: newCategory.name.trim(),
        isImageId: !!imageId,
        imageId: imageId
      };

      console.log('📤 Sending add category request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/add_category', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Add category response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Category created successfully!', 'Success');
        setNewCategory({ name: '', imageId: '', imageUrl: '', sNo: 1 });
        setSelectedFile(null);
        setPreviewUrl('');
        setAddCategoryOpen(false);
        
        // Refresh the categories list to show the latest data
        console.log('🔄 Refreshing categories list...');
        await fetchCategories();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to create category'}`, 'Create Category Error');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      showErrorPopup('Error adding category. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Handle individual category status toggle
  const handleCategoryStatusToggle = async (categoryId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      alert('Session data not found. Please login again.');
      return;
    }

    try {
      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'disable_category' : 'enable_category';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling category ${categoryId} from ${currentStatus} to ${newStatus} using ${endpoint}`);

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          categoryId
        }),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ Category ${newStatus ? 'enabled' : 'disabled'} successfully`);
        
        // Refresh the categories list to show the updated status
        await fetchCategories();
      } else {
        console.error(`❌ Failed to ${newStatus ? 'enable' : 'disable'} category:`, result.message);
        showErrorPopup(`Error: ${result.message || `Failed to ${newStatus ? 'enable' : 'disable'} category`}`, 'Status Update Error');
      }
    } catch (error) {
      console.error('Error updating category status:', error);
      showErrorPopup('Error updating category status. Please try again.', 'Network Error');
    }
  };

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleEdit = () => {
    console.log('Edit category:', selectedCategory);
    setEditCategory({
      categoryId: selectedCategory.categoryId,
      name: selectedCategory.name,
      imageId: selectedCategory.imageId || '',
      imageUrl: selectedCategory.imageUrl || '',
      sNo: selectedCategory.sNo || 1
    });
    setEditPreviewUrl(selectedCategory.imageUrl || '');
    setEditSelectedFile(null);
    setEditCategoryOpen(true);
    handleMenuClose();
  };

  const handleEditCategory = async () => {
    if (!editCategory.name.trim()) {
      showWarningPopup('Please enter a category name', 'Category Name Required');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      let imageId = editCategory.imageId;
      let imageUrl = editCategory.imageUrl;

      // Upload new image if selected
      if (editSelectedFile) {
        setUploadingImage(true);
        try {
          console.log('🔄 Starting image upload for edit file:', editSelectedFile.name);
          const uploadResult = await uploadImage(editSelectedFile);
          
          if (!uploadResult || !uploadResult._id) {
            throw new Error('Upload result is missing _id field');
          }
          
          imageId = uploadResult._id;
          imageUrl = uploadResult.s3FileURL || '';
          
          console.log('✅ Image uploaded successfully for edit:', {
            imageId,
            imageUrl,
            fullResult: uploadResult
          });
        } catch (error) {
          console.error('❌ Error uploading image for edit:', error);
          showErrorPopup(`Error uploading image: ${error.message}`, 'Upload Error');
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        categoryId: editCategory.categoryId,
        sNo: editCategory.sNo,
        name: editCategory.name.trim(),
        isImageId: !!imageId,
        imageId: imageId
      };

      console.log('📤 Sending edit category request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/edit_category', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Edit category response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Category updated successfully!', 'Success');
        setEditCategory({ categoryId: '', name: '', imageId: '', imageUrl: '', sNo: 1 });
        setEditSelectedFile(null);
        setEditPreviewUrl('');
        setEditCategoryOpen(false);
        
        // Refresh the categories list to show the latest data
        console.log('🔄 Refreshing categories list...');
        await fetchCategories();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to update category'}`, 'Update Category Error');
      }
    } catch (error) {
      console.error('Error editing category:', error);
      showErrorPopup('Error editing category. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    console.log('Delete category:', selectedCategory);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Categories
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddCategoryOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Add Category
        </Button>
      </Box>
      
      {/* Filter and Search Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={activeToggle}
                onChange={(e) => setActiveToggle(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#1976d2',
                  },
                }}
              />
            }
            label="Active"
            sx={{ color: '#1976d2', fontWeight: 500 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {totalPages > 0 && (
            <>
              <Chip 
                label={`Total: ${totalCount}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
              />
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '0.75rem',
                      minWidth: '28px',
                      height: '28px',
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading categories...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No categories found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category, index) => (
                <TableRow key={category.categoryId}>
                  <TableCell>{category.sNo || index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {category.imageUrl ? (
                        <Box
                          component="img"
                          src={category.imageUrl}
                          alt={category.name}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 1, 
                            mr: 2,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 1, 
                            mr: 2,
                            bgcolor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">📁</Typography>
                        </Box>
                      )}
                      <Typography variant="body1">{category.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={category.isActive === true}
                      onChange={() => handleCategoryStatusToggle(category.categoryId, category.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, category)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Category Dialog */}
      <Dialog open={addCategoryOpen} onClose={() => setAddCategoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={newCategory.sNo}
            onChange={(e) => setNewCategory({...newCategory, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          />
          <TextField
            label="Category Name"
            fullWidth
            margin="normal"
            value={newCategory.name}
            onChange={(e) => setNewCategory({...newCategory, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter category name..."
            autoFocus
            required
          />
          
          {/* Image Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Category Image (Optional)
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="category-image-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="category-image-upload">
              <Button
                variant="outlined"
                component="span"
                sx={{ mb: 2 }}
                disabled={uploadingImage}
              >
                {uploadingImage ? 'Uploading...' : 'Choose Image'}
              </Button>
            </label>
            
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid #ddd'
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                  }}
                  sx={{ ml: 1 }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddCategoryOpen(false);
            setNewCategory({ name: '', imageId: '', imageUrl: '', sNo: 1 });
            setSelectedFile(null);
            setPreviewUrl('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained"
            disabled={loading || !newCategory.name.trim() || uploadingImage}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Adding...' : 'Add Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryOpen} onClose={() => setEditCategoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={editCategory.sNo}
            onChange={(e) => setEditCategory({...editCategory, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          />
          <TextField
            label="Category Name"
            fullWidth
            margin="normal"
            value={editCategory.name}
            onChange={(e) => setEditCategory({...editCategory, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter category name..."
            autoFocus
            required
          />
          
          {/* Image Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Category Image (Optional)
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="edit-category-image-upload"
              type="file"
              onChange={handleEditFileSelect}
            />
            <label htmlFor="edit-category-image-upload">
              <Button
                variant="outlined"
                component="span"
                sx={{ mb: 2 }}
                disabled={uploadingImage}
              >
                {uploadingImage ? 'Uploading...' : 'Choose New Image'}
              </Button>
            </label>
            
            {editPreviewUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={editPreviewUrl}
                  alt="Preview"
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid #ddd'
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setEditSelectedFile(null);
                    setEditPreviewUrl(editCategory.imageUrl || '');
                  }}
                  sx={{ ml: 1 }}
                >
                  Remove New Image
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditCategoryOpen(false);
            setEditCategory({ categoryId: '', name: '', imageId: '', imageUrl: '', sNo: 1 });
            setEditSelectedFile(null);
            setEditPreviewUrl('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditCategory} 
            variant="contained"
            disabled={loading || !editCategory.name.trim() || uploadingImage}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Updating...' : 'Update Category'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedCategory && selectedCategory.isActive === true ? (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// Ads View Component
function AdsView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addAdOpen, setAddAdOpen] = useState(false);
  const [newAd, setNewAd] = useState({
    title: '',
    addType: 1,
    positionNo: '',
    url: '',
    startDate: '',
    endDate: '',
    imageDimensions: '330x550'
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageId, setImageId] = useState(null);
  const [ads, setAds] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedAddListType, setSelectedAddListType] = useState(''); // '' = All, 'national' = 1,2, 'local' = 3,4
  
  // Publish dialog state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishPositionNo, setPublishPositionNo] = useState('');
  const [selectedAdForPublish, setSelectedAdForPublish] = useState(null);
  
  // Location selection fields (similar to Article.js)
  const [newsType, setNewsType] = useState(1); // 1: National, 2: State, 3: District, 4: Constituency
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [fetchingStates, setFetchingStates] = useState(true);
  const [fetchingDistricts, setFetchingDistricts] = useState(false);
  const [fetchingConstituencies, setFetchingConstituencies] = useState(false);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Ads:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch ads data
  const fetchAds = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for ads');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      // Build addListType filter based on selection
      // National: addListType 1, 2 (National and State level ads)
      // Local: addListType 3, 4 (District and Constituency level ads)
      let addListTypeFilter = null;
      if (selectedAddListType === 'national') {
        // Filter for National ads: addListType 1 and 2
        addListTypeFilter = [1, 2];
      } else if (selectedAddListType === 'local') {
        // Filter for Local ads: addListType 3 and 4
        addListTypeFilter = [3, 4];
      }

      const requestBody = {
          sessionId,
          dashAdminId,
        skip: (currentPage - 1) * pageSize, // Convert to 0-based index
          limit: pageSize,
          isActiveFilter: true, // Always filter by active status
          isActive: activeToggle, // Show active ads when toggle is on, inactive when toggle is off
          isSearchFilter: searchTerm.trim() !== "",
          search: searchTerm.trim()
      };

      // Add addListType filter if selected
      // National: addListType 1, 2 (National and State level ads)
      // Local: addListType 3, 4 (District and Constituency level ads)
      if (addListTypeFilter && Array.isArray(addListTypeFilter) && addListTypeFilter.length > 0) {
        requestBody.isaddListTypeFilter = true;
        requestBody.addListType = addListTypeFilter;
      } else {
        // Explicitly set to false when no filter is selected to show all ads
        requestBody.isaddListTypeFilter = false;
      }

      console.log('📤 Fetching ads with filter:', {
        selectedAddListType,
        addListTypeFilter,
        isaddListTypeFilter: requestBody.isaddListTypeFilter,
        addListType: requestBody.addListType
      });
      console.log('📤 Full request body:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_adds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('📥 Ads fetched response:', result);
        if (result.data && Array.isArray(result.data)) {
          // Log addListType values for debugging
          console.log('📊 Ads addListType values:', result.data.map(ad => ({
            title: ad.title,
            addListType: ad.addListType,
            addId: ad.addId || ad._id || ad.id
          })));
          
          // Apply client-side filtering as fallback if API doesn't filter correctly
          let filteredAds = result.data;
          if (addListTypeFilter && Array.isArray(addListTypeFilter) && addListTypeFilter.length > 0) {
            filteredAds = result.data.filter(ad => {
              const adListType = ad.addListType;
              const matches = addListTypeFilter.includes(adListType);
              if (!matches) {
                console.log(`❌ Ad "${ad.title}" (addListType: ${adListType}) filtered out. Expected: ${addListTypeFilter.join(', ')}`);
              }
              return matches;
            });
            console.log(`🔍 Client-side filter applied: ${result.data.length} → ${filteredAds.length} ads`);
          }
          
          setAds(filteredAds);
          
          // Set total count and calculate total pages
          const total = result.totalCount || filteredAds.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setAds([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching ads:', errorData);
        setAds([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      setAds([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, searchTerm, selectedAddListType]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedAddListType, activeToggle]);

  // Fetch states from API
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingStates(false);
      return;
    }

    setFetchingStates(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isSearchFilter: false,
          search: "",
          sortBy: 'sNo',
          sortOrder: 'asc'
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched for ads:', result);
        if (result.data && Array.isArray(result.data)) {
          setStates(result.data);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingStates(false);
    }
  }, []);

  // Fetch districts from API
  const fetchDistricts = useCallback(async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingDistricts(false);
      return;
    }

    setFetchingDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched for ads:', result);
        if (result.data && Array.isArray(result.data)) {
          setDistricts(result.data);
        } else {
          setDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setFetchingDistricts(false);
    }
  }, []);

  // Fetch constituencies from API
  const fetchConstituencies = useCallback(async (districtId) => {
    if (!districtId) {
      setConstituencies([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for constituencies');
      setFetchingConstituencies(false);
      return;
    }

    setFetchingConstituencies(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_constituencies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: false,
          stateId: "",
          isdistrictIdFilter: true,
          districtId: districtId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Constituencies fetched for ads:', result);
        if (result.data && Array.isArray(result.data)) {
          setConstituencies(result.data);
        } else {
          setConstituencies([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching constituencies:', errorData);
        setConstituencies([]);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
    } finally {
      setFetchingConstituencies(false);
    }
  }, []);

  // Handle news type change
  const handleNewsTypeChange = (event) => {
    const newNewsType = parseInt(event.target.value);
    setNewsType(newNewsType);
    
    // Reset state, district, and constituency selections when news type changes
    if (newNewsType === 1) { // National
      setSelectedState('');
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 2) { // State
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 3) { // District
      setSelectedConstituency('');
      setConstituencies([]);
    }
  };

  // Handle state change
  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedState(stateId);
    setSelectedDistrict(''); // Reset district when state changes
    setSelectedConstituency(''); // Reset constituency when state changes
    
    if (stateId && (newsType === 3 || newsType === 4)) { // Fetch districts if news type is District or Constituency
      fetchDistricts(stateId);
    } else {
      setDistricts([]);
    }
    setConstituencies([]);
  };

  // Handle district change
  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedConstituency(''); // Reset constituency when district changes
    
    if (districtId && newsType === 4) { // Only fetch constituencies if news type is Constituency
      fetchConstituencies(districtId);
    } else {
      setConstituencies([]);
    }
  };

  // Fetch states on component mount
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  // Handle open publish dialog
  const handleOpenPublishDialog = (ad) => {
    const addId = ad.addId || ad._id || ad.id;
    setSelectedAdForPublish({ addId, ad });
    setPublishPositionNo('');
    setPublishDialogOpen(true);
  };

  // Handle close publish dialog
  const handleClosePublishDialog = () => {
    setPublishDialogOpen(false);
    setPublishPositionNo('');
    setSelectedAdForPublish(null);
  };

  // Handle publish ad
  const handlePublishAd = async () => {
    if (!selectedAdForPublish) {
      showErrorPopup('Ad information not found. Cannot publish ad.', 'Invalid Ad');
      return;
    }

    const { addId } = selectedAdForPublish;
    
    // Validate position number
    if (publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null) {
      showWarningPopup('Please enter a position number (0-99)', 'Position Number Required');
      return;
    }

    const positionNum = typeof publishPositionNo === 'string' ? parseInt(publishPositionNo) : publishPositionNo;
    if (isNaN(positionNum) || positionNum < 0 || positionNum > 99) {
      showWarningPopup('Position number must be between 0-99', 'Invalid Position Number');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    if (!addId) {
      showErrorPopup('Ad ID not found. Cannot publish ad.', 'Invalid Ad');
      return;
    }

    try {
      console.log(`🔄 Publishing ad ${addId} with positionNo ${positionNum}`);

      const response = await fetch('https://api.jpnewz.com/dashboard/publish_add', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          addId,
          positionNo: positionNum
        }),
      });

      const result = await response.json();
      console.log('📥 publish_add response:', result);

      if (response.ok && result.status === true) {
        console.log('✅ Ad published successfully');
        showSuccessPopup(result.message || 'Ad published successfully.', 'Success');
        
        // Close dialog and refresh the ads list
        handleClosePublishDialog();
        await fetchAds();
      } else {
        console.error('❌ Failed to publish ad:', result.message);
        showErrorPopup(`Error: ${result.message || 'Failed to publish ad'}`, 'Publish Error');
      }
    } catch (error) {
      console.error('Error publishing ad:', error);
      showErrorPopup('Error publishing ad. Please try again.', 'Network Error');
    }
  };

  // Handle unpublish ad
  const handleUnpublishAd = async (addId, positionNo) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    if (!addId) {
      showErrorPopup('Ad ID not found. Cannot unpublish ad.', 'Invalid Ad');
      return;
    }

    try {
      console.log(`🔄 Unpublishing ad ${addId} with positionNo ${positionNo}`);

      const response = await fetch('https://api.jpnewz.com/dashboard/unpublish_add', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          addId,
          positionNo: positionNo || 1
        }),
      });

      const result = await response.json();
      console.log('📥 unpublish_add response:', result);

      if (response.ok && result.status === true) {
        console.log('✅ Ad unpublished successfully');
        showSuccessPopup(result.message || 'Ad unpublished successfully.', 'Success');
        
        // Refresh the ads list to show the updated status
        await fetchAds();
      } else {
        console.error('❌ Failed to unpublish ad:', result.message);
        showErrorPopup(`Error: ${result.message || 'Failed to unpublish ad'}`, 'Unpublish Error');
      }
    } catch (error) {
      console.error('Error unpublishing ad:', error);
      showErrorPopup('Error unpublishing ad. Please try again.', 'Network Error');
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Upload image function
  const uploadImage = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading image with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    const response = await fetch('https://api.jpnewz.com/upload/upload_image', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    console.log('📥 Upload image response:', result);

    if (!response.ok || !result.status) {
      throw new Error(result.message || 'Failed to upload image');
    }

    if (!result.data) {
      console.error('Upload response missing data:', result);
      throw new Error('Invalid response from upload server');
    }

    if (!result.data._id || !result.data.s3FileURL) {
      console.error('Upload response missing required fields:', result.data);
      throw new Error('Upload response missing required fields (_id or s3FileURL)');
    }

    return result.data;
  };

  const handleAddAd = async () => {
    if (!newAd.title.trim()) {
      showWarningPopup('Please enter an ad title', 'Ad Title Required');
      return;
    }

    if (!newAd.url.trim()) {
      showWarningPopup('Please enter an ad URL', 'Ad URL Required');
      return;
    }

    if (!newAd.startDate) {
      showWarningPopup('Please select a start date', 'Start Date Required');
      return;
    }

    if (!newAd.endDate) {
      showWarningPopup('Please select an end date', 'End Date Required');
      return;
    }

    if (!selectedFile) {
      showWarningPopup('Please select an image for the ad', 'Image Required');
      return;
    }

    // Validate location fields based on news type
    if (newsType === 2 && !selectedState) {
      showWarningPopup('Please select a state for state-level ad', 'State Required');
      return;
    }
    if (newsType === 3 && !selectedState) {
      showWarningPopup('Please select a state for district-level ad', 'State Required');
      return;
    }
    if (newsType === 3 && !selectedDistrict) {
      showWarningPopup('Please select a district for district-level ad', 'District Required');
      return;
    }
    if (newsType === 4 && !selectedState) {
      showWarningPopup('Please select a state for constituency-level ad', 'State Required');
      return;
    }
    if (newsType === 4 && !selectedDistrict) {
      showWarningPopup('Please select a district for constituency-level ad', 'District Required');
      return;
    }
    if (newsType === 4 && !selectedConstituency) {
      showWarningPopup('Please select a constituency for constituency-level ad', 'Constituency Required');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      let uploadedImageId = null;

      // Upload image if selected
      if (selectedFile) {
        setUploadingImage(true);
        try {
          console.log('🔄 Starting image upload for file:', selectedFile.name);
          const uploadResult = await uploadImage(selectedFile);
          
          if (!uploadResult || !uploadResult._id) {
            throw new Error('Upload result is missing _id field');
          }
          
          uploadedImageId = uploadResult._id;
          setImageId(uploadedImageId);
          
          console.log('✅ Image uploaded successfully:', {
            imageId: uploadedImageId,
            imageUrl: uploadResult.s3FileURL || '',
            fullResult: uploadResult
          });
        } catch (error) {
          console.error('❌ Error uploading image:', error);
          showErrorPopup(`Error uploading image: ${error.message}`, 'Upload Error');
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      // Format dates to ISO string
      const startDateISO = new Date(newAd.startDate).toISOString();
      const endDateISO = new Date(newAd.endDate).toISOString();

      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        addType: newAd.addType,
        addListType: newsType, // Location type: 1=National, 2=State, 3=District, 4=Constituency (API expects addListType, not newsType)
        title: newAd.title.trim(),
        // positionNo removed - not required from user input, will be set when publishing
        url: newAd.url.trim(),
        startDate: startDateISO,
        endDate: endDateISO,
        imageDimenstions: newAd.imageDimensions,
        stateId: newsType === 1 ? null : selectedState, // null for national, selected state for state/district/constituency
        districtId: (newsType === 3 || newsType === 4) ? selectedDistrict : null, // only set for district/constituency
        constituencyId: newsType === 4 ? selectedConstituency : null, // only set for constituency
        mediaArray: [
          {
            mediaType: 1,
            imageId: uploadedImageId,
            videoId: null
          }
        ]
      };

      console.log('📤 Sending create ad request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/create_add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Create ad response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Ad created successfully!', 'Success');
        setNewAd({
          title: '',
          addType: 1,
          positionNo: '',
          url: '',
          startDate: '',
          endDate: '',
          imageDimensions: '330x550'
        });
        setSelectedFile(null);
        setPreviewUrl('');
        setImageId(null);
        setAddAdOpen(false);
        
        // Refresh the ads list to show the latest data
        console.log('🔄 Refreshing ads list...');
        await fetchAds();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to create ad'}`, 'Create Ad Error');
      }
    } catch (error) {
      console.error('Error creating ad:', error);
      showErrorPopup('Error creating ad. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Ads
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddAdOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Create Ad
        </Button>
      </Box>
      
      {/* Filter and Search Controls */}
      <Box sx={{ mb: 3 }}>
        {/* Add List Type Filter Buttons with Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={selectedAddListType === '' ? 'contained' : 'outlined'}
              onClick={() => setSelectedAddListType('')}
              sx={{
                minWidth: 100,
                textTransform: 'none',
                ...(selectedAddListType === '' ? {
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1565c0',
                  }
                } : {
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                  }
                })
              }}
            >
              All
            </Button>
            <Button
              variant={selectedAddListType === 'national' ? 'contained' : 'outlined'}
              onClick={() => setSelectedAddListType('national')}
              sx={{
                minWidth: 120,
                textTransform: 'none',
                ...(selectedAddListType === 'national' ? {
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1565c0',
                  }
                } : {
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                  }
                })
              }}
            >
              National
            </Button>
            <Button
              variant={selectedAddListType === 'local' ? 'contained' : 'outlined'}
              onClick={() => setSelectedAddListType('local')}
              sx={{
                minWidth: 100,
                textTransform: 'none',
                ...(selectedAddListType === 'local' ? {
                  bgcolor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#1565c0',
                  }
                } : {
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                  }
                })
              }}
            >
              Local
            </Button>
          </Box>
          
          {/* Pagination Controls - Parallel to Filter Buttons */}
          {totalPages > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`Total: ${totalCount}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
              />
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '0.75rem',
                      minWidth: '28px',
                      height: '28px',
                    },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Location Type</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading ads...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : ads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No ads found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              ads.map((ad) => {
                // Get the addId from various possible field names
                const addId = ad.addId || ad._id || ad.id;
                return (
                  <TableRow key={addId || ad.title}>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {ad.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ad.addType === 1 ? 'Full Image' : 'Banner'} 
                        size="small" 
                        sx={{
                          bgcolor: ad.addType === 1 ? '#e3f2fd' : '#f3e5f5',
                          color: ad.addType === 1 ? '#1976d2' : '#9c27b0',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={
                          ad.addListType === 1 ? 'National' :
                          ad.addListType === 2 ? 'State' :
                          ad.addListType === 3 ? 'District' :
                          ad.addListType === 4 ? 'Constituency' :
                          `Type ${ad.addListType || 'N/A'}`
                        }
                        size="small" 
                        sx={{
                          bgcolor: 
                            ad.addListType === 1 || ad.addListType === 2 ? '#e8f5e9' : 
                            ad.addListType === 3 || ad.addListType === 4 ? '#fff3e0' : '#f5f5f5',
                          color: 
                            ad.addListType === 1 || ad.addListType === 2 ? '#2e7d32' : 
                            ad.addListType === 3 || ad.addListType === 4 ? '#e65100' : '#666',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1976d2', 
                          textDecoration: 'none',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {ad.url}
                      </Typography>
                    </TableCell>
                    <TableCell>{ad.positionNo}</TableCell>
                    <TableCell>
                      {ad.startDate ? new Date(ad.startDate).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {ad.endDate ? new Date(ad.endDate).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>{ad.imageDimenstions || ad.imageDimensions || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {ad.isPublished ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<UnpublishedIcon />}
                            onClick={() => handleUnpublishAd(addId, ad.positionNo)}
                            sx={{ textTransform: 'none' }}
                          >
                            Unpublish
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<CampaignIcon />}
                            onClick={() => handleOpenPublishDialog(ad)}
                            sx={{ textTransform: 'none' }}
                          >
                            Publish
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Ad Dialog */}
      <Dialog 
        open={addAdOpen} 
        onClose={() => setAddAdOpen(false)} 
        maxWidth="md" 
        fullWidth
        scroll="body"
        disableScrollLock
      >
        <DialogTitle>Create Ad</DialogTitle>
        <DialogContent>
          <TextField
            label="Ad Title"
            fullWidth
            margin="normal"
            value={newAd.title}
            onChange={(e) => setNewAd({...newAd, title: e.target.value})}
            placeholder="Enter ad title..."
            required
          />
          <TextField
            label="URL"
            fullWidth
            margin="normal"
            value={newAd.url}
            onChange={(e) => setNewAd({...newAd, url: e.target.value})}
            placeholder="https://justpostnews.com"
            required
          />
          
          {/* Position No field removed - not required from user input */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Add Type</InputLabel>
              <Select
                value={newAd.addType}
                onChange={(e) => setNewAd({...newAd, addType: e.target.value})}
                label="Add Type"
              >
                <MenuItem value={1}>Full Image</MenuItem>
                <MenuItem value={2}>Banner</MenuItem>
              </Select>
            </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              label="Start Date"
              type="datetime-local"
              fullWidth
              margin="normal"
              value={newAd.startDate}
              onChange={(e) => setNewAd({...newAd, startDate: e.target.value})}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16)
              }}
              required
            />
            <TextField
              label="End Date"
              type="datetime-local"
              fullWidth
              margin="normal"
              value={newAd.endDate}
              onChange={(e) => setNewAd({...newAd, endDate: e.target.value})}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: newAd.startDate || new Date().toISOString().slice(0, 16)
              }}
              required
            />
          </Box>

          {/* Image Dimensions field hidden - auto-assigned by backend */}
          {/* <TextField
            label="Image Dimensions"
            fullWidth
            margin="normal"
            value={newAd.imageDimensions}
            onChange={(e) => setNewAd({...newAd, imageDimensions: e.target.value})}
            placeholder="330x550"
            required
          /> */}
          
          {/* Location Selection (similar to Article.js) */}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Location Type</InputLabel>
              <Select
                value={newsType}
                onChange={handleNewsTypeChange}
                label="Location Type"
              >
                <MenuItem value={1}>National</MenuItem>
                <MenuItem value={2}>State</MenuItem>
                <MenuItem value={3}>District</MenuItem>
                <MenuItem value={4}>Constituency</MenuItem>
              </Select>
            </FormControl>

            {/* State Selection - Show for State, District, or Constituency */}
            {(newsType === 2 || newsType === 3 || newsType === 4) && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>State</InputLabel>
                <Select
                  value={selectedState}
                  onChange={handleStateChange}
                  label="State"
                  disabled={fetchingStates}
                >
                  {states.map((state) => (
                    <MenuItem key={state.stateId} value={state.stateId}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* District Selection - Show for District or Constituency */}
            {(newsType === 3 || newsType === 4) && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  onChange={handleDistrictChange}
                  label="District"
                  disabled={fetchingDistricts || !selectedState}
                >
                  {districts.map((district) => (
                    <MenuItem key={district.districtId} value={district.districtId}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Constituency Selection - Show only for Constituency */}
            {newsType === 4 && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Constituency</InputLabel>
                <Select
                  value={selectedConstituency}
                  onChange={(e) => setSelectedConstituency(e.target.value)}
                  label="Constituency"
                  disabled={fetchingConstituencies || !selectedDistrict}
                >
                  {constituencies.map((constituency) => (
                    <MenuItem key={constituency.constituencyId} value={constituency.constituencyId}>
                      {constituency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          
          {/* Image Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Ad Image (Required)
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="ad-image-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="ad-image-upload">
              <Button
                variant="outlined"
                component="span"
                sx={{ mb: 2 }}
                disabled={uploadingImage}
              >
                {uploadingImage ? 'Uploading...' : 'Choose Image'}
              </Button>
            </label>
            
            {previewUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{ 
                    width: 200, 
                    height: 200, 
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid #ddd'
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                    setImageId(null);
                  }}
                  sx={{ ml: 1 }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddAdOpen(false);
            setNewAd({
              title: '',
              addType: 1,
              positionNo: '',
              url: '',
              startDate: '',
              endDate: '',
              imageDimensions: '330x550'
            });
            setSelectedFile(null);
            setPreviewUrl('');
            setImageId(null);
            // Reset location fields
            setNewsType(1);
            setSelectedState('');
            setSelectedDistrict('');
            setSelectedConstituency('');
            setDistricts([]);
            setConstituencies([]);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddAd} 
            variant="contained"
            disabled={
              loading || 
              uploadingImage ||
              !newAd.title.trim() || 
              !newAd.url.trim() || 
              !newAd.startDate || 
              !newAd.endDate || 
              !selectedFile ||
              !newAd.addType ||
              (newsType === 2 && !selectedState) ||
              (newsType === 3 && (!selectedState || !selectedDistrict)) ||
              (newsType === 4 && (!selectedState || !selectedDistrict || !selectedConstituency))
            }
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Creating...' : 'Create Ad'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Ad Dialog - Position Number Input */}
      <Dialog 
        open={publishDialogOpen} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click if position number is not valid
          if (reason === 'backdropClick' && (publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null || 
              (typeof publishPositionNo === 'string' && publishPositionNo !== '' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99)))) {
            return;
          }
          handleClosePublishDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish Ad</DialogTitle>
        <DialogContent>
          <TextField
            key={publishDialogOpen ? 'publish-position-input' : 'publish-position-input-closed'}
            label="Position No *"
            type="number"
            fullWidth
            margin="normal"
            value={publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null ? '' : String(publishPositionNo)}
            onChange={(e) => {
              const value = e.target.value;
              console.log('📝 Position number input changed:', value);
              if (value === '') {
                setPublishPositionNo('');
                return;
              }
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 0 && numValue <= 99) {
                setPublishPositionNo(numValue);
              } else {
                // Invalid value, keep as string to show error
                setPublishPositionNo(value);
              }
            }}
            inputProps={{ 
              min: 0,
              max: 99,
              step: 1
            }}
            placeholder="Enter position number (0-99)"
            required
            autoFocus
            error={publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null || 
                   (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99)) ||
                   (typeof publishPositionNo === 'string' && publishPositionNo !== '' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99))}
            helperText={
              publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null 
                ? 'Position number is required (0-99)' 
                : (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99) 
                  ? 'Position number must be between 0-99' 
                  : (typeof publishPositionNo === 'string' && publishPositionNo !== '' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99)
                    ? 'Please enter a valid number between 0-99'
                    : ''))
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter the position number for publishing this ad (0-99)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePublishDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublishAd}
            disabled={
              publishPositionNo === '' || 
              publishPositionNo === undefined || 
              publishPositionNo === null ||
              (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99)) ||
              (typeof publishPositionNo === 'string' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99))
            }
            startIcon={<CampaignIcon />}
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// States View Component
function StatesView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addStateOpen, setAddStateOpen] = useState(false);
  const [editStateOpen, setEditStateOpen] = useState(false);
  const [newState, setNewState] = useState({ name: '', sNo: 1, languageId: 'L100' });
  const [editState, setEditState] = useState({ stateId: '', name: '', sNo: 1, languageId: 'L100' });
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage] = useState(0);
  const [pageSize] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  
  // Commented out static data
  // const states = [
  //   { id: 1, name: 'Andhra Pradesh', status: 'Active' },
  //   { id: 2, name: 'Telangana', status: 'InActive' },
  //   { id: 3, name: 'Karnataka', status: 'Active' },
  //   { id: 4, name: 'Tamil Nadu', status: 'Active' },
  //   { id: 5, name: 'Kerala', status: 'Active' },
  //   { id: 6, name: 'Maharashtra', status: 'Inactive' },
  //   { id: 7, name: 'Delhi', status: 'Active' },
  //   { id: 8, name: 'Gujarat', status: 'Active' },
  // ];

  // Use states directly since filtering is now done server-side, but sort by sNo as fallback
  const filteredStates = states.sort((a, b) => {
    const aSNo = a.sNo || 999; // Default to high number if sNo is missing
    const bSNo = b.sNo || 999;
    return aSNo - bSNo; // Sort in ascending order
  });

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for States:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch states data
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: currentPage * pageSize,
          limit: pageSize,
          isActiveFilter: true, // Always filter by active status
          isActive: activeToggle, // Show active states when toggle is on, inactive when toggle is off
          islanguageIdFilter: false,
          languageId: "",
          isSearchFilter: searchTerm.trim() !== "",
          search: searchTerm.trim(),
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setStates(result.data);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, searchTerm]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  // Handle individual state status toggle
  const handleStateStatusToggle = async (stateId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      alert('Session data not found. Please login again.');
      return;
    }

    try {
      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'disable_state' : 'enable_state';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling state ${stateId} from ${currentStatus} to ${newStatus} using ${endpoint}`);

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          stateId
        }),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ State ${newStatus ? 'enabled' : 'disabled'} successfully`);
        
        // Refresh the states list to show the updated status
        await fetchStates();
      } else {
        console.error(`❌ Failed to ${newStatus ? 'enable' : 'disable'} state:`, result.message);
        alert(`Error: ${result.message || `Failed to ${newStatus ? 'enable' : 'disable'} state`}`);
      }
    } catch (error) {
      console.error('Error updating state status:', error);
      alert('Error updating state status. Please try again.');
    }
  };

  const handleAddState = async () => {
    if (!newState.name.trim()) {
      showWarningPopup('Please enter a state name', 'State Name Required');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        sNo: newState.sNo,
        name: newState.name.trim(),
        islanguageId: true,
        languageId: newState.languageId
      };

      console.log('📤 Sending add state request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/add_state', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Add state response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('State created successfully!', 'Success');
        setNewState({ name: '', sNo: 1, languageId: 'L100' });
        setAddStateOpen(false);
        
        // Refresh the states list to show the latest data
        console.log('🔄 Refreshing states list...');
        await fetchStates();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to create state'}`, 'Create State Error');
      }
    } catch (error) {
      console.error('Error adding state:', error);
      showErrorPopup('Error adding state. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, state) => {
    setAnchorEl(event.currentTarget);
    setSelectedState(state);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedState(null);
  };

  const handleEdit = () => {
    console.log('Edit state:', selectedState);
    setEditState({
      stateId: selectedState.stateId,
      name: selectedState.name,
      sNo: selectedState.sNo || 1,
      languageId: selectedState.languageId || 'L100'
    });
    setEditStateOpen(true);
    handleMenuClose();
  };

  const handleEditState = async () => {
    if (!editState.name.trim()) {
      showWarningPopup('Please enter a state name', 'State Name Required');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        stateId: editState.stateId,
        sNo: editState.sNo,
        name: editState.name.trim(),
        islanguageId: true,
        languageId: editState.languageId
      };

      console.log('📤 Sending edit state request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/edit_state', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Edit state response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('State updated successfully!', 'Success');
        setEditState({ stateId: '', name: '', sNo: 1, languageId: 'L100' });
        setEditStateOpen(false);
        
        // Refresh the states list to show the latest data
        console.log('🔄 Refreshing states list...');
        await fetchStates();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to update state'}`, 'Update State Error');
      }
    } catch (error) {
      console.error('Error editing state:', error);
      showErrorPopup('Error editing state. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    console.log('Delete state:', selectedState);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          States
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddStateOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Add State
        </Button>
      </Box>
      
      {/* Filter and Search Controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label="Active"
          sx={{ color: '#1976d2', fontWeight: 500 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* S.No column hidden - auto-assigned by backend */}
              {/* <TableCell>S.No</TableCell> */}
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading states...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredStates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No states found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStates.map((state, index) => (
                <TableRow key={state.stateId}>
                  {/* S.No column hidden - auto-assigned by backend */}
                  {/* <TableCell>{state.sNo || index + 1}</TableCell> */}
                  <TableCell>
                    <Typography variant="body1">{state.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={state.isActive === true}
                      onChange={() => handleStateStatusToggle(state.stateId, state.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, state)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add State Dialog */}
      <Dialog open={addStateOpen} onClose={() => setAddStateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add State</DialogTitle>
        <DialogContent>
          {/* S.No field hidden - auto-assigned by backend */}
          {/* <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={newState.sNo}
            onChange={(e) => setNewState({...newState, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          /> */}
          <TextField
            label="State Name"
            fullWidth
            margin="normal"
            value={newState.name}
            onChange={(e) => setNewState({...newState, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter state name..."
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddStateOpen(false);
            setNewState({ name: '', sNo: 1, languageId: 'L100' });
          }}>Cancel</Button>
          <Button 
            onClick={handleAddState} 
            variant="contained"
            disabled={loading || !newState.name.trim()}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Adding...' : 'Add State'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit State Dialog */}
      <Dialog open={editStateOpen} onClose={() => setEditStateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit State</DialogTitle>
        <DialogContent>
          {/* S.No field hidden - auto-assigned by backend */}
          {/* <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={editState.sNo}
            onChange={(e) => setEditState({...editState, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          /> */}
          <TextField
            label="State Name"
            fullWidth
            margin="normal"
            value={editState.name}
            onChange={(e) => setEditState({...editState, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter state name..."
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditStateOpen(false);
            setEditState({ stateId: '', name: '', sNo: 1, languageId: 'L100' });
          }}>Cancel</Button>
          <Button 
            onClick={handleEditState} 
            variant="contained"
            disabled={loading || !editState.name.trim()}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Updating...' : 'Update State'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Quality Managers View Component
function QualityManagersView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addQualityManagerOpen, setAddQualityManagerOpen] = useState(false);
  const [newQualityManager, setNewQualityManager] = useState({ name: '', phone: '', email: '', roleType: 2, emailotp: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [qualityManagers, setQualityManagers] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage] = useState(0);
  const [pageSize] = useState(10);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');



  // Get session data from localStorage or props
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch quality managers data
  const fetchQualityManagers = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found');
      console.log('🔍 Available localStorage keys:', Object.keys(localStorage));
      console.log('🔍 localStorage content:', {
        sessionId: localStorage.getItem('sessionId'),
        dashAdminId: localStorage.getItem('dashAdminId'),
        allKeys: Object.keys(localStorage)
      });
      
      // Try to get session data from user object if available
      const userSessionId = localStorage.getItem('userSessionId');
      const userDashAdminId = localStorage.getItem('userDashAdminId');
      
      if (userSessionId && userDashAdminId) {
        console.log('✅ Found session data in user-specific keys');
        localStorage.setItem('sessionId', userSessionId);
        localStorage.setItem('dashAdminId', userDashAdminId);
        // Retry the fetch with the found session data
        setTimeout(() => fetchQualityManagers(), 100);
        return;
      }
      
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_dashboard_admins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: currentPage * pageSize,
          limit: pageSize,
          isActiveFilter: true,
          isActive: activeToggle,
          isRoleTypeFilter: true,
          roleType: 2, // Filter for Quality Managers only
          isSearchFilter: searchTerm.trim() !== "",
          search: searchTerm.trim()
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Quality managers fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setQualityManagers(result.data);
        } else {
          setQualityManagers([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching quality managers:', errorData);
        setQualityManagers([]);
      }
    } catch (error) {
      console.error('Error fetching quality managers:', error);
      setQualityManagers([]);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, searchTerm]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchQualityManagers();
  }, [fetchQualityManagers]);

  // Handle individual quality manager status toggle
  const handleQualityManagerStatusToggle = async (managerId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      alert('Session data not found. Please login again.');
      return;
    }

    try {
      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'inactive_dashboard_admin' : 'active_dashboard_admin';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling quality manager ${managerId} from ${currentStatus} to ${newStatus} using ${endpoint}`);
      console.log(`🔄 User's dashAdminId: ${managerId}`);
      console.log(`🔄 Current user's dashAdminId: ${dashAdminId}`);
      console.log(`🔄 selected_dashAdminId will be: ${managerId}`);

      const requestBody = currentStatus 
        ? {
            sessionId,
            dashAdminId,
            selected_dashAdminId: managerId
          }
        : {
            sessionId,
            dashAdminId,
            selected_dashAdminId: managerId  // Pass the user's dashAdminId for activation too
          };

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ Quality Manager ${newStatus ? 'activated' : 'deactivated'} successfully`);
        
        // Refresh the quality managers list to show the updated status
        await fetchQualityManagers();
        
        // Update toggle state based on the new status
        console.log(`🔄 Current toggle state: ${activeToggle ? 'Active' : 'Inactive'}`);
        console.log(`🔄 Role type filter: 2 (Quality Managers)`);
      } else {
        console.error(`❌ Failed to ${newStatus ? 'activate' : 'deactivate'} quality manager:`, result.message);
        alert(`Error: ${result.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} quality manager`}`);
      }
    } catch (error) {
      console.error('Error updating quality manager status:', error);
      alert('Error updating quality manager status. Please try again.');
    }
  };

  // Use qualityManagers directly since filtering is now done server-side
  const filteredQualityManagers = qualityManagers;

  // Validation functions
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    if (!phone.trim()) {
      setPhoneError('');
      return true;
    }
    if (!phoneRegex.test(phone.trim())) {
      setPhoneError('Phone number must be exactly 10 digits');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!email.trim()) {
      setEmailError('');
      return true;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError('Email must be a valid Gmail address (e.g., example@gmail.com)');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleVerifyEmail = async () => {
    if (!newQualityManager.email.trim()) {
      alert('Please enter an email address first');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newQualityManager.email.trim())) {
      alert('Please enter a valid Gmail address (e.g., example@gmail.com)');
      return;
    }

    setVerifyingEmail(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/send_email_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newQualityManager.email }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Email verification initiated:', result);
        setEmailVerified(true);
        alert('OTP sent to your email. Please check and enter the OTP.');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to send OTP'}`);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert('Error sending OTP. Please try again.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleAddQualityManager = async () => {
    if (!newQualityManager.name.trim() || !newQualityManager.email.trim() || !newQualityManager.phone.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate phone number - must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(newQualityManager.phone.trim())) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newQualityManager.email.trim())) {
      alert('Please enter a valid Gmail address (e.g., example@gmail.com)');
      return;
    }

    if (!emailVerified || !newQualityManager.emailotp.trim()) {
      alert('Please verify your email with OTP first');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/create_dashboard_admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newQualityManager),
      });

             if (response.ok) {
         const result = await response.json();
         console.log('Quality Manager added successfully:', result);
         setNewQualityManager({ name: '', phone: '', email: '', roleType: 2, emailotp: '' });
         setEmailVerified(false);
         setAddQualityManagerOpen(false);
         
         // Show success message
         alert('Quality Manager added successfully!');
         
         // Refresh the quality managers list to show the latest data
         console.log('🔄 Refreshing quality managers list...');
         await fetchQualityManagers();
       } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to add quality manager'}`);
      }
    } catch (error) {
      console.error('Error adding quality manager:', error);
      alert('Error adding quality manager. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, manager) => {
    setAnchorEl(event.currentTarget);
    setSelectedManager(manager);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedManager(null);
  };

  const handleEdit = () => {
    console.log('Edit quality manager:', selectedManager);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete quality manager:', selectedManager);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Quality Managers
        </Typography>
         <Button 
           variant="contained" 
           onClick={() => setAddQualityManagerOpen(true)}
           sx={{ bgcolor: '#1976d2' }}
         >
           + Add Quality Manager
         </Button>
      </Box>
      
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label={activeToggle ? "Active" : "Inactive"}
          sx={{ color: '#1976d2', fontWeight: 500 }}
        />
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
                     <TableBody>
             {fetchingData ? (
               <TableRow>
                 <TableCell colSpan={6} align="center">
                   <Typography variant="body2" color="text.secondary">
                     Loading quality managers...
                   </Typography>
                 </TableCell>
               </TableRow>
             ) : filteredQualityManagers.length === 0 ? (
               <TableRow>
                 <TableCell colSpan={6} align="center">
                   <Typography variant="body2" color="text.secondary">
                     No quality managers found
                   </Typography>
                 </TableCell>
               </TableRow>
             ) : (
               filteredQualityManagers.map((manager, index) => (
              <TableRow key={manager.dashAdminId}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{manager.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{manager.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{manager.phone}</Typography>
                </TableCell>
                                   <TableCell>
                    <Switch
                      checked={manager.isActive === true}
                      onChange={() => handleQualityManagerStatusToggle(manager.dashAdminId, manager.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(event) => handleMenuOpen(event, manager)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

             {/* Add Quality Manager Dialog */}
       <Dialog open={addQualityManagerOpen} onClose={() => setAddQualityManagerOpen(false)} maxWidth="sm" fullWidth>
         <DialogTitle>Add Quality Manager</DialogTitle>
         <DialogContent>
           <TextField
             label="Name"
             fullWidth
             margin="normal"
             value={newQualityManager.name}
             onChange={(e) => setNewQualityManager({...newQualityManager, name: capitalizeFirstLetter(e.target.value)})}
             placeholder="Enter full name..."
             autoFocus
           />
                       <TextField
              label="Phone"
              fullWidth
              margin="normal"
              value={newQualityManager.phone}
              onChange={(e) => {
                setNewQualityManager({...newQualityManager, phone: e.target.value});
                validatePhone(e.target.value);
              }}
              onBlur={(e) => validatePhone(e.target.value)}
              placeholder="Enter phone number..."
              error={!!phoneError}
              helperText={phoneError}
            />
                       <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                             <TextField
                 label="Email"
                 fullWidth
                 margin="normal"
                 value={newQualityManager.email}
                 onChange={(e) => {
                   setNewQualityManager({...newQualityManager, email: e.target.value});
                   validateEmail(e.target.value);
                 }}
                 onBlur={(e) => validateEmail(e.target.value)}
                 placeholder="Enter email address..."
                 type="email"
                 error={!!emailError}
                 helperText={emailError}
               />
              <Button
                variant="outlined"
                onClick={handleVerifyEmail}
                disabled={verifyingEmail || !newQualityManager.email.trim()}
                sx={{ mb: 1, minWidth: 100 }}
              >
                {verifyingEmail ? 'Sending...' : 'Verify'}
              </Button>
            </Box>
            {emailVerified && (
              <TextField
                label="Email OTP"
                fullWidth
                margin="normal"
                value={newQualityManager.emailotp}
                onChange={(e) => setNewQualityManager({...newQualityManager, emailotp: e.target.value})}
                placeholder="Enter email OTP..."
              />
            )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setAddQualityManagerOpen(false)}>Cancel</Button>
                       <Button 
              onClick={handleAddQualityManager} 
              variant="contained"
              disabled={loading || !newQualityManager.name.trim() || !newQualityManager.email.trim() || !newQualityManager.phone.trim() || !emailVerified || !newQualityManager.emailotp.trim()}
              sx={{ bgcolor: '#1976d2' }}
            >
              {loading ? 'Adding...' : 'Add Quality Manager'}
            </Button>
         </DialogActions>
       </Dialog>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedManager && selectedManager.isActive === true ? (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// Approvers View Component
function ApproversView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addApproverOpen, setAddApproverOpen] = useState(false);
  const [newApprover, setNewApprover] = useState({ name: '', phone: '', email: '', roleType: 4, emailotp: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage] = useState(0);
  const [pageSize] = useState(10);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Get session data from localStorage or props
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Approvers:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch approvers data
  const fetchApprovers = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for approvers');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const requestBody = {
        sessionId,
        dashAdminId,
        skip: currentPage * pageSize,
        limit: pageSize,
        isActiveFilter: true, // Always filter by active status
        isActive: activeToggle, // Show active approvers when toggle is on, inactive when toggle is off
        isRoleTypeFilter: true,
        roleType: 4, // Filter for Approvers only
        isSearchFilter: searchTerm.trim() !== "",
        search: searchTerm.trim()
      };

      console.log('📤 Fetching approvers with:', requestBody);
      console.log('📤 RoleType being sent:', requestBody.roleType);
      console.log('📤 isRoleTypeFilter being sent:', requestBody.isRoleTypeFilter);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_dashboard_admins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Approvers fetched:', result);
        console.log('Response status:', response.status);
        console.log('Response data:', result.data);
        console.log('Data type:', typeof result.data);
        console.log('Is array:', Array.isArray(result.data));
        if (result.data && Array.isArray(result.data)) {
          console.log('Setting approvers:', result.data);
          setApprovers(result.data);
        } else {
          console.log('No valid data, setting empty array');
          setApprovers([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching approvers:', errorData);
        console.error('Response status:', response.status);
        setApprovers([]);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
      setApprovers([]);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, searchTerm]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchApprovers();
  }, [fetchApprovers]);

  // Handle individual approver status toggle
  const handleApproverStatusToggle = async (approverId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      alert('Session data not found. Please login again.');
      return;
    }

    try {
      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'inactive_dashboard_admin' : 'active_dashboard_admin';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling approver ${approverId} from ${currentStatus} to ${newStatus} using ${endpoint}`);

      const requestBody = {
        sessionId,
        dashAdminId,
        selected_dashAdminId: approverId
      };

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ Approver ${newStatus ? 'activated' : 'deactivated'} successfully`);
        
        // Refresh the approvers list to show the updated status
        await fetchApprovers();
      } else {
        console.error(`❌ Failed to ${newStatus ? 'activate' : 'deactivate'} approver:`, result.message);
        alert(`Error: ${result.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} approver`}`);
      }
    } catch (error) {
      console.error('Error updating approver status:', error);
      alert('Error updating approver status. Please try again.');
    }
  };

  // Use approvers directly since filtering is now done server-side
  const filteredApprovers = approvers;

  // Validation functions
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    if (!phone.trim()) {
      setPhoneError('');
      return true;
    }
    if (!phoneRegex.test(phone.trim())) {
      setPhoneError('Phone number must be exactly 10 digits');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!email.trim()) {
      setEmailError('');
      return true;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError('Email must be a valid Gmail address (e.g., example@gmail.com)');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleVerifyEmail = async () => {
    if (!newApprover.email.trim()) {
      alert('Please enter an email address first');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newApprover.email.trim())) {
      alert('Please enter a valid Gmail address (e.g., example@gmail.com)');
      return;
    }

    setVerifyingEmail(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/send_email_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newApprover.email }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Email verification initiated:', result);
        setEmailVerified(true);
        alert('OTP sent to your email. Please check and enter the OTP.');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to send OTP'}`);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert('Error sending OTP. Please try again.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleAddApprover = async () => {
    if (!newApprover.name.trim() || !newApprover.email.trim() || !newApprover.phone.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate phone number - must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(newApprover.phone.trim())) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newApprover.email.trim())) {
      alert('Please enter a valid Gmail address (e.g., example@gmail.com)');
      return;
    }

    if (!emailVerified || !newApprover.emailotp.trim()) {
      alert('Please verify your email with OTP first');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/create_dashboard_admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newApprover),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Approver added successfully:', result);
        setNewApprover({ name: '', phone: '', email: '', roleType: 4, emailotp: '' });
        setEmailVerified(false);
        setAddApproverOpen(false);
        
        // Show success message
        alert('Approver added successfully!');
        
        // Refresh the approvers list to show the latest data
        console.log('🔄 Refreshing approvers list...');
        await fetchApprovers();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to add approver'}`);
      }
    } catch (error) {
      console.error('Error adding approver:', error);
      alert('Error adding approver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, approver) => {
    setAnchorEl(event.currentTarget);
    setSelectedApprover(approver);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedApprover(null);
  };

  const handleEdit = () => {
    console.log('Edit approver:', selectedApprover);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete approver:', selectedApprover);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Approvers
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddApproverOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Add Approver
        </Button>
      </Box>
      
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label={activeToggle ? "Active" : "Inactive"}
          sx={{ color: '#1976d2', fontWeight: 500 }}
        />
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading approvers...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredApprovers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No approvers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredApprovers.map((approver, index) => (
                <TableRow key={approver.dashAdminId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{approver.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{approver.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{approver.phone}</Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={approver.isActive === true}
                      onChange={() => handleApproverStatusToggle(approver.dashAdminId, approver.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, approver)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Approver Dialog */}
      <Dialog open={addApproverOpen} onClose={() => setAddApproverOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Approver</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newApprover.name}
            onChange={(e) => setNewApprover({...newApprover, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter full name..."
            autoFocus
          />
          <TextField
            label="Phone"
            fullWidth
            margin="normal"
            value={newApprover.phone}
            onChange={(e) => {
              setNewApprover({...newApprover, phone: e.target.value});
              validatePhone(e.target.value);
            }}
            onBlur={(e) => validatePhone(e.target.value)}
            placeholder="Enter phone number..."
            error={!!phoneError}
            helperText={phoneError}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={newApprover.email}
              onChange={(e) => {
                setNewApprover({...newApprover, email: e.target.value});
                validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              placeholder="Enter email address..."
              type="email"
              error={!!emailError}
              helperText={emailError}
            />
            <Button
              variant="outlined"
              onClick={handleVerifyEmail}
              disabled={verifyingEmail || !newApprover.email.trim()}
              sx={{ mb: 1, minWidth: 100 }}
            >
              {verifyingEmail ? 'Sending...' : 'Verify'}
            </Button>
          </Box>
          {emailVerified && (
            <TextField
              label="Email OTP"
              fullWidth
              margin="normal"
              value={newApprover.emailotp}
              onChange={(e) => setNewApprover({...newApprover, emailotp: e.target.value})}
              placeholder="Enter email OTP..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddApproverOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddApprover} 
            variant="contained"
            disabled={loading || !newApprover.name.trim() || !newApprover.email.trim() || !newApprover.phone.trim() || !emailVerified || !newApprover.emailotp.trim()}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Adding...' : 'Add Approver'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedApprover && selectedApprover.isActive === true ? (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

// News View Component
function NewsView({ onEditArticle }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPostStatus, setSelectedPostStatus] = useState(''); // Add post status filter state
  const [posts, setPosts] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedDeleteDate, setSelectedDeleteDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for News:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch posts data
  const fetchPosts = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for posts');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const requestBody = {
        sessionId,
        dashAdminId,
        skip: (currentPage - 1) * pageSize, // Convert to 0-based index
        limit: pageSize,
        isSearchFilter: searchTerm.trim() !== "",
        search: searchTerm.trim(),
        ispostStatusFilter: selectedPostStatus !== "",
        postStatus: selectedPostStatus
      };

      console.log('📤 Fetching posts with:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Posts fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort posts by createdAt in descending order (latest created first)
          const sortedPosts = result.data.sort((a, b) => {
            // Use createdAt as primary sorting parameter
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateB - dateA; // Descending order (latest created first)
          });
          setPosts(sortedPosts);
          
          // Set total count and calculate total pages
          const total = result.totalCount || result.data.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setPosts([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching posts:', errorData);
        setPosts([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, searchTerm, selectedPostStatus]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Get post type label
  const getPostTypeLabel = (postType) => {
    switch (postType) {
      case 1: return 'News';
      case 2: return 'Article';
      case 3: return 'Video';
      default: return 'Unknown';
    }
  };

  // Get news type label
  const getNewsTypeLabel = (newsType) => {
    switch (newsType) {
      case 1: return 'Breaking';
      case 2: return 'Regular';
      default: return 'Unknown';
    }
  };

  // Get post status label
  const getPostStatusLabel = (postStatus) => {
    switch (postStatus) {
      case 1: return 'Verification';
      case 2: return 'Approved';
      case 3: return 'Rejected';
      case 4: return 'Published';
      case 5: return 'Unpublished';
      default: return 'Unknown';
    }
  };

  // Get post status color
  const getPostStatusColor = (postStatus) => {
    switch (postStatus) {
      case 1: return 'warning.main'; // Pending for verification - orange
      case 2: return 'success.main'; // Post Approved - green
      case 3: return 'error.main';   // Post Rejected - red
      case 4: return 'primary.main'; // Post Published - blue
      case 5: return 'secondary.main'; // Post Unpublished - gray
      default: return 'text.secondary';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return { date: dateStr, time: timeStr };
  };

  const handleMenuOpen = (event, post) => {
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  const handleEdit = () => {
    console.log('Edit post:', selectedPost);
    if (selectedPost && onEditArticle) {
      onEditArticle(selectedPost.postId, selectedPost);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete post:', selectedPost);
    handleMenuClose();
  };

  // Handle unpublish post
  const handleUnpublish = async () => {
    if (!selectedPost) {
      console.error('No post selected for unpublishing');
      return;
    }

    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/unpublish_post', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          postId: selectedPost.postId
        }),
      });

      const result = await response.json();
      console.log('Unpublish post response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Post unpublished successfully!', 'Success');
        // Refresh the posts list
        fetchPosts();
      } else {
        showErrorPopup(`Failed to unpublish post: ${result.message || 'Unknown error'}`, 'Error');
      }
    } catch (error) {
      console.error('Error unpublishing post:', error);
      showErrorPopup(`Error unpublishing post: ${error.message}`, 'Network Error');
    } finally {
      handleMenuClose();
    }
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Reset to first page when search term or post status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPostStatus]);

  // Validate selected date - must be at least 10 days old
  const validateDeleteDate = (dateString) => {
    if (!dateString) {
      setDateError('Please select a date');
      return false;
    }

    const selectedDate = new Date(dateString);
    const currentDate = new Date();
    const tenDaysAgo = new Date(currentDate);
    tenDaysAgo.setDate(currentDate.getDate() - 10);
    tenDaysAgo.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate >= tenDaysAgo) {
      setDateError('Date must be at least 10 days before today');
      return false;
    }

    setDateError('');
    return true;
  };

  // Handle date change
  const handleDateChange = (event) => {
    const dateValue = event.target.value;
    setSelectedDeleteDate(dateValue);
    if (dateValue) {
      validateDeleteDate(dateValue);
    } else {
      setDateError('');
    }
  };

  // Handle delete posts before date
  const handleDeletePostsBeforeDate = async () => {
    // Validate date first
    if (!validateDeleteDate(selectedDeleteDate)) {
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setDeleting(true);
    try {
      // Format the selected date as ISO format for the API
      const deleteBeforeDate = new Date(selectedDeleteDate).toISOString();

      const requestBody = {
        dashAdminId,
        sessionId,
        deleteBeforeDate
      };

      console.log('📤 Deleting posts before date:', {
        ...requestBody,
        selectedDeleteDate: selectedDeleteDate,
        deleteBeforeDateISO: deleteBeforeDate
      });

      const response = await fetch('https://api.jpnewz.com/dashboard/delete_posts_before_date', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Delete posts response:', result);

      if (response.ok && result.status === true) {
        const count = result.count || 0;
        showSuccessPopup(
          `Posts deleted successfully. ${count} post(s) were deleted.`,
          'Success'
        );
        // Refresh the posts list
        fetchPosts();
        // Reset form
        setSelectedDeleteDate('');
        setDateError('');
      } else {
        showErrorPopup(
          `Failed to delete posts: ${result.message || 'Unknown error'}`,
          'Error'
        );
      }
    } catch (error) {
      console.error('Error deleting posts:', error);
      showErrorPopup(
        `Error deleting posts: ${error.message}`,
        'Network Error'
      );
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteButtonClick = () => {
    // Reset date and error when opening dialog
    setSelectedDeleteDate('');
    setDateError('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setSelectedDeleteDate('');
      setDateError('');
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600, mb: 2 }}>
          News Posts
        </Typography>
        
        {/* Post Status Filter Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant={selectedPostStatus === '' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            All
          </Button>
          <Button
            variant={selectedPostStatus === '1' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('1')}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              ...(selectedPostStatus === '1' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Verification
          </Button>
          <Button
            variant={selectedPostStatus === '2' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('2')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '2' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Approved
          </Button>
          <Button
            variant={selectedPostStatus === '3' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('3')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '3' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Rejected
          </Button>
          <Button
            variant={selectedPostStatus === '4' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('4')}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              ...(selectedPostStatus === '4' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Published
          </Button>
          <Button
            variant={selectedPostStatus === '5' ? 'contained' : 'outlined'}
            onClick={() => setSelectedPostStatus('5')}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              ...(selectedPostStatus === '5' ? {
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              } : {
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                }
              })
            }}
          >
            Unpublished
          </Button>
          {/* Delete Button - Only for Super Admin */}
          <Button
            variant="contained"
            onClick={handleDeleteButtonClick}
            disabled={deleting}
            startIcon={<DeleteIcon />}
            sx={{
              minWidth: 150,
              textTransform: 'none',
              bgcolor: '#d32f2f',
              color: 'white',
              '&:hover': {
                bgcolor: '#c62828',
              },
              '&:disabled': {
                bgcolor: '#ef5350',
                color: 'white',
              }
            }}
          >
            {deleting ? 'Deleting...' : 'Delete Old Posts'}
          </Button>
        </Box>
      </Box>
      
      {/* Search Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
            <TextField
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
          </Box>
          {/* Commented out Per Page selection logic */}
          {/* <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label="Per Page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl> */}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            label={`Total: ${totalCount}`} 
            color="primary" 
            variant="outlined" 
            size="small" 
          />
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
          {/* Pagination Controls - Beside total count */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.75rem',
                  minWidth: '28px',
                  height: '28px',
                },
              }}
            />
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Created Date</TableCell>
              <TableCell>Media</TableCell>
              <TableCell>Title & Description</TableCell>
              <TableCell>Reporter Name</TableCell>
              <TableCell>Engagement</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading posts...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No posts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post, index) => (
                <TableRow 
                  key={post.postId}
                  onClick={() => {
                    console.log('🖱️ Article row clicked:', post);
                    if (onEditArticle) {
                      console.log('📤 Calling onEditArticle with:', post.postId, post);
                      onEditArticle(post.postId, post);
                    } else {
                      console.log('❌ onEditArticle function not available');
                    }
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <TableCell sx={{ width: '120px' }}>
                    {formatDate(post.createdAt) !== 'N/A' ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {formatDate(post.createdAt).date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatDate(post.createdAt).time}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {post.mediaArray && post.mediaArray.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {post.mediaArray.slice(0, 2).map((media, mediaIndex) => (
                          <Box
                            key={mediaIndex}
                            component="img"
                            src={media.imageUrl}
                            alt={`Media ${mediaIndex + 1}`}
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '1px solid #ddd'
                            }}
                          />
                        ))}
                        {post.mediaArray.length > 2 && (
                          <Box
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 1,
                              bgcolor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #ddd'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              +{post.mediaArray.length - 2}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No media
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.95rem' }}>
                        {post.title}
                      </Typography>
                      {post.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {post.description}
                        </Typography>
                      )}
                      {post.hashTags && (
                        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                          {post.hashTags}
                        </Typography>
                      )}
                      {/* News Type and Post Status under title/description */}
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {getNewsTypeLabel(post.newsType)}
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: getPostStatusColor(post.postStatus) === 'warning.main' ? '#fff3e0' :
                                     getPostStatusColor(post.postStatus) === 'success.main' ? '#e8f5e8' :
                                     getPostStatusColor(post.postStatus) === 'error.main' ? '#ffebee' :
                                     getPostStatusColor(post.postStatus) === 'secondary.main' ? '#f5f5f5' :
                                     '#e3f2fd',
                            border: `1px solid ${
                              getPostStatusColor(post.postStatus) === 'warning.main' ? '#ff9800' :
                              getPostStatusColor(post.postStatus) === 'success.main' ? '#4caf50' :
                              getPostStatusColor(post.postStatus) === 'error.main' ? '#f44336' :
                              getPostStatusColor(post.postStatus) === 'secondary.main' ? '#9e9e9e' :
                              '#2196f3'
                            }`,
                            display: 'inline-block'
                          }}
                        >
                          <Typography 
                            variant="caption"
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              color: getPostStatusColor(post.postStatus) === 'warning.main' ? '#e65100' :
                                     getPostStatusColor(post.postStatus) === 'success.main' ? '#2e7d32' :
                                     getPostStatusColor(post.postStatus) === 'error.main' ? '#d32f2f' :
                                     getPostStatusColor(post.postStatus) === 'secondary.main' ? '#616161' :
                                     '#1565c0'
                            }}
                          >
                            {getPostStatusLabel(post.postStatus)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {post.createdAdminName || 'Unknown Reporter'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        👍 {post.noOfLikes || 0} | 👎 {post.noOfDisLikes || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        📖 {post.noOfBookmarks || 0} | 👁️ {post.noOfViews || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation(); // Prevent row click
                        handleMenuOpen(event, post);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {/* Unpublish option - Only for published posts */}
        {selectedPost && selectedPost.postStatus === 4 && (
          <MenuItem onClick={handleUnpublish}>
            <UnpublishedIcon fontSize="small" sx={{ mr: 1 }} />
            Unpublish
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Posts Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete Old Posts
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Select a date to delete all posts created before that date. The date must be at least 10 days before today.
          </Typography>
          
          <TextField
            label="Select Date"
            type="date"
            value={selectedDeleteDate}
            onChange={handleDateChange}
            fullWidth
            required
            error={!!dateError}
            helperText={dateError || 'Select a date that is at least 10 days before today'}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: (() => {
                const maxDate = new Date();
                maxDate.setDate(maxDate.getDate() - 10);
                return maxDate.toISOString().split('T')[0];
              })(),
            }}
            sx={{ mb: 2 }}
            disabled={deleting}
          />
          
          {selectedDeleteDate && !dateError && (
            <Box sx={{ 
              p: 2, 
              bgcolor: '#fff3e0', 
              borderRadius: 1, 
              mb: 2,
              border: '1px solid #ff9800'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                ⚠️ Warning
              </Typography>
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                All posts created before {new Date(selectedDeleteDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} will be permanently deleted. This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDialogClose}
            disabled={deleting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeletePostsBeforeDate}
            disabled={deleting || !selectedDeleteDate || !!dateError}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Posts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Districts View Component
function DistrictsView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addDistrictOpen, setAddDistrictOpen] = useState(false);
  const [editDistrictOpen, setEditDistrictOpen] = useState(false);
  const [newDistrict, setNewDistrict] = useState({ name: '', stateId: '', sNo: 1 });
  const [editingDistrict, setEditingDistrict] = useState({ name: '', stateId: '', districtId: '', sNo: 1 });
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [states, setStates] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [fetchingStates, setFetchingStates] = useState(true);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Districts:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch states data for dropdown
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingStates(false);
      return;
    }

    setFetchingStates(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100, // Get all states for dropdown
          isActiveFilter: true,
          isActive: true,
          islanguageIdFilter: false,
          languageId: "",
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched for dropdown:', result);
        if (result.data && Array.isArray(result.data)) {
          setStates(result.data);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingStates(false);
    }
  }, []);

  // Fetch districts data
  const fetchDistricts = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const requestBody = {
        sessionId,
        dashAdminId,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        isActiveFilter: true, // Always filter by active status
        isActive: activeToggle, // Show active districts when toggle is on, inactive when toggle is off
        isstateIdFilter: selectedStateId !== '',
        stateId: selectedStateId,
        isSearchFilter: searchTerm.trim() !== "",
        search: searchTerm.trim(),
        sortBy: 'sNo', // Sort by sNo for ranking
        sortOrder: 'asc' // Ascending order for ranking
      };

      console.log('📤 Fetching districts with:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort districts by sNo for ranking
          const sortedDistricts = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999; // Default to high number if sNo is missing
            const bSNo = b.sNo || 999;
            return aSNo - bSNo; // Sort in ascending order
          });
          setDistricts(sortedDistricts);
          const total = result.totalCount || result.data.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setDistricts([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setDistricts([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, selectedStateId, searchTerm]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStateId, activeToggle]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  const handleAddDistrict = async () => {
    if (!newDistrict.name.trim() || !newDistrict.stateId) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        stateId: newDistrict.stateId,
        sNo: newDistrict.sNo,
        name: newDistrict.name.trim()
      };

      console.log('📤 Sending add district request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/add_district', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Add district response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('District created successfully!', 'Success');
        setNewDistrict({ name: '', stateId: '', sNo: 1 });
        setAddDistrictOpen(false);
        
        // Refresh the districts list to show the latest data
        console.log('🔄 Refreshing districts list...');
        await fetchDistricts();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to create district'}`, 'Create District Error');
      }
    } catch (error) {
      console.error('Error adding district:', error);
      showErrorPopup('Error adding district. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Get state name by stateId
  const getStateName = (stateId) => {
    const state = states.find(s => s.stateId === stateId);
    return state ? state.name : 'Unknown State';
  };

  // Handle individual district status toggle
  const handleDistrictStatusToggle = async (districtId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      alert('Session data not found. Please login again.');
      return;
    }

    try {
      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'disable_district' : 'enable_district';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling district ${districtId} from ${currentStatus} to ${newStatus} using ${endpoint}`);

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          districtId
        }),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ District ${newStatus ? 'enabled' : 'disabled'} successfully`);
        
        // Refresh the districts list to show the updated status
        await fetchDistricts();
      } else {
        console.error(`❌ Failed to ${newStatus ? 'enable' : 'disable'} district:`, result.message);
        alert(`Error: ${result.message || `Failed to ${newStatus ? 'enable' : 'disable'} district`}`);
      }
    } catch (error) {
      console.error('Error updating district status:', error);
      alert('Error updating district status. Please try again.');
    }
  };

  const handleMenuOpen = (event, district) => {
    setAnchorEl(event.currentTarget);
    setSelectedDistrict(district);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDistrict(null);
  };

  const handleEdit = () => {
    if (selectedDistrict) {
      setEditingDistrict({
        name: selectedDistrict.name,
        stateId: selectedDistrict.stateId,
        districtId: selectedDistrict.districtId,
        sNo: selectedDistrict.sNo || 1
      });
      setEditDistrictOpen(true);
    }
    handleMenuClose();
  };

  const handleEditDistrict = async () => {
    if (!editingDistrict.name.trim() || !editingDistrict.stateId) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        districtId: editingDistrict.districtId,
        sNo: editingDistrict.sNo,
        name: editingDistrict.name.trim()
      };

      console.log('📤 Sending edit district request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/edit_district', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Edit district response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('District updated successfully!', 'Success');
        setEditingDistrict({ name: '', stateId: '', districtId: '', sNo: 1 });
        setEditDistrictOpen(false);
        
        // Refresh the districts list to show the latest data
        console.log('🔄 Refreshing districts list...');
        await fetchDistricts();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to update district'}`, 'Update District Error');
      }
    } catch (error) {
      console.error('Error editing district:', error);
      showErrorPopup('Error editing district. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    console.log('Delete district:', selectedDistrict);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Districts
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddDistrictOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Add District
        </Button>
      </Box>
      
      {/* Pagination Controls - Top Row */}
      {totalPages > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${totalCount}`} 
            color="primary" 
            variant="outlined" 
            size="small"
            sx={{ fontSize: '0.75rem', height: '24px' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            Page {currentPage} of {totalPages}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.75rem',
                  minWidth: '28px',
                  height: '28px',
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Filter and Search Controls - Bottom Row */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label="Active"
          sx={{ color: '#1976d2', fontWeight: 500, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>State</Typography>
          <TextField
            select
            value={selectedStateId}
            onChange={(e) => setSelectedStateId(e.target.value)}
            size="small"
            sx={{ 
              minWidth: { xs: 140, sm: 160, md: 180 },
              maxWidth: { xs: 180, sm: 200 },
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                height: '36px',
              },
            }}
            disabled={fetchingStates}
          >
            <MenuItem value="">
              <em>All States</em>
            </MenuItem>
            {states.map((state) => (
              <MenuItem key={state.stateId} value={state.stateId}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              width: { xs: 140, sm: 160, md: 180 },
              maxWidth: { xs: 180, sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.875rem',
                height: '36px',
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* S.No column hidden - auto-assigned by backend */}
              {/* <TableCell>S.No</TableCell> */}
              <TableCell>District</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading districts...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : districts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No districts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              districts.map((district, index) => (
                <TableRow key={district.districtId}>
                  {/* S.No column hidden - auto-assigned by backend */}
                  {/* <TableCell>{district.sNo || index + 1}</TableCell> */}
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{district.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {district.stateName || getStateName(district.stateId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={district.isActive === true}
                      onChange={() => handleDistrictStatusToggle(district.districtId, district.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, district)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add District Dialog */}
      <Dialog open={addDistrictOpen} onClose={() => setAddDistrictOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add District</DialogTitle>
        <DialogContent>
          {/* S.No field hidden - auto-assigned by backend */}
          {/* <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={newDistrict.sNo}
            onChange={(e) => setNewDistrict({...newDistrict, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          /> */}
          <TextField
            select
            label="State"
            fullWidth
            margin="normal"
            value={newDistrict.stateId}
            onChange={(e) => setNewDistrict({...newDistrict, stateId: e.target.value})}
            disabled={fetchingStates}
            required
          >
            {states.map((state) => (
              <MenuItem key={state.stateId} value={state.stateId}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="District Name"
            fullWidth
            margin="normal"
            value={newDistrict.name}
            onChange={(e) => setNewDistrict({...newDistrict, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter district name..."
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDistrictOpen(false);
            setNewDistrict({ name: '', stateId: '', sNo: 1 });
          }}>Cancel</Button>
          <Button 
            onClick={handleAddDistrict} 
            variant="contained"
            disabled={loading || !newDistrict.name.trim() || !newDistrict.stateId}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Adding...' : 'Add District'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit District Dialog */}
      <Dialog open={editDistrictOpen} onClose={() => setEditDistrictOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit District</DialogTitle>
        <DialogContent>
          {/* S.No field hidden - auto-assigned by backend */}
          {/* <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={editingDistrict.sNo}
            onChange={(e) => setEditingDistrict({...editingDistrict, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          /> */}
          <TextField
            select
            label="State"
            fullWidth
            margin="normal"
            value={editingDistrict.stateId}
            onChange={(e) => setEditingDistrict({...editingDistrict, stateId: e.target.value})}
            disabled={fetchingStates}
            required
          >
            {states.map((state) => (
              <MenuItem key={state.stateId} value={state.stateId}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="District Name"
            fullWidth
            margin="normal"
            value={editingDistrict.name}
            onChange={(e) => setEditingDistrict({...editingDistrict, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter district name..."
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDistrictOpen(false);
            setEditingDistrict({ name: '', stateId: '', districtId: '', sNo: 1 });
          }}>Cancel</Button>
          <Button 
            onClick={handleEditDistrict} 
            variant="contained"
            disabled={loading || !editingDistrict.name.trim() || !editingDistrict.stateId}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Updating...' : 'Update District'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Constituencies View Component
function ConstituenciesView() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [addConstituencyOpen, setAddConstituencyOpen] = useState(false);
  const [editConstituencyOpen, setEditConstituencyOpen] = useState(false);
  const [newConstituency, setNewConstituency] = useState({ name: '', districtId: '', sNo: '' });
  const [editingConstituency, setEditingConstituency] = useState({ name: '', districtId: '', constituencyId: '', sNo: 1 });
  const [loading, setLoading] = useState(false);
  const [constituencies, setConstituencies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [addDistricts, setAddDistricts] = useState([]); // Separate districts for add dialog
  const [editDistricts, setEditDistricts] = useState([]); // Separate districts for edit dialog
  const [states, setStates] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [fetchingDistricts, setFetchingDistricts] = useState(true);
  const [fetchingAddDistricts, setFetchingAddDistricts] = useState(false);
  const [fetchingEditDistricts, setFetchingEditDistricts] = useState(false);
  const [fetchingStates, setFetchingStates] = useState(true);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [addStateId, setAddStateId] = useState(''); // Separate state for add dialog
  const [editStateId, setEditStateId] = useState(''); // Separate state for edit dialog
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConstituency, setSelectedConstituency] = useState(null);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Constituencies:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch states data for dropdown
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingStates(false);
      return;
    }

    setFetchingStates(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          islanguageIdFilter: false,
          languageId: "",
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched for dropdown:', result);
        if (result.data && Array.isArray(result.data)) {
          setStates(result.data);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingStates(false);
    }
  }, []);

  // Fetch districts data for filter dropdown
  const fetchDistricts = useCallback(async (stateId) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingDistricts(false);
      return;
    }

    if (!stateId) {
      setDistricts([]);
      setFetchingDistricts(false);
      return;
    }

    setFetchingDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched for dropdown:', result);
        if (result.data && Array.isArray(result.data)) {
          setDistricts(result.data);
        } else {
          setDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setFetchingDistricts(false);
    }
  }, []);

  // Fetch districts for add dialog
  const fetchAddDistricts = useCallback(async (stateId) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingAddDistricts(false);
      return;
    }

    if (!stateId) {
      setAddDistricts([]);
      setFetchingAddDistricts(false);
      return;
    }

    setFetchingAddDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched for add dialog:', result);
        if (result.data && Array.isArray(result.data)) {
          setAddDistricts(result.data);
        } else {
          setAddDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setAddDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setAddDistricts([]);
    } finally {
      setFetchingAddDistricts(false);
    }
  }, []);

  // Fetch districts for edit dialog
  const fetchEditDistricts = useCallback(async (stateId) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingEditDistricts(false);
      return;
    }

    if (!stateId) {
      setEditDistricts([]);
      setFetchingEditDistricts(false);
      return;
    }

    setFetchingEditDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched for edit dialog:', result);
        if (result.data && Array.isArray(result.data)) {
          setEditDistricts(result.data);
        } else {
          setEditDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setEditDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setEditDistricts([]);
    } finally {
      setFetchingEditDistricts(false);
    }
  }, []);

  // Fetch constituencies data
  const fetchConstituencies = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for constituencies');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const requestBody = {
        sessionId,
        dashAdminId,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        isActiveFilter: true,
        isActive: activeToggle,
        isstateIdFilter: selectedStateId !== '',
        stateId: selectedStateId,
        isdistrictIdFilter: selectedDistrictId !== '',
        districtId: selectedDistrictId,
        isSearchFilter: searchTerm.trim() !== "",
        search: searchTerm.trim()
      };

      console.log('📤 Fetching constituencies with:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_constituencies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Constituencies fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          const sortedConstituencies = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999;
            const bSNo = b.sNo || 999;
            return aSNo - bSNo;
          });
          setConstituencies(sortedConstituencies);
          const total = result.totalCount || result.data.length;
          setTotalCount(total);
          setTotalPages(Math.ceil(total / pageSize));
        } else {
          setConstituencies([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching constituencies:', errorData);
        setConstituencies([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setFetchingData(false);
    }
  }, [currentPage, pageSize, activeToggle, selectedStateId, selectedDistrictId, searchTerm]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStateId, selectedDistrictId, activeToggle]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    if (selectedStateId) {
      fetchDistricts(selectedStateId);
    } else {
      setDistricts([]);
    }
  }, [selectedStateId, fetchDistricts]);

  // Fetch add districts when add state changes
  useEffect(() => {
    if (addStateId) {
      fetchAddDistricts(addStateId);
    } else {
      setAddDistricts([]);
    }
  }, [addStateId, fetchAddDistricts]);

  // Fetch edit districts when edit state changes
  useEffect(() => {
    if (editStateId) {
      fetchEditDistricts(editStateId);
    } else {
      setEditDistricts([]);
    }
  }, [editStateId, fetchEditDistricts]);

  useEffect(() => {
    fetchConstituencies();
  }, [fetchConstituencies]);

  const handleAddConstituency = async () => {
    // sNo is auto-assigned by backend, no need to validate it
    if (!newConstituency.name.trim() || !newConstituency.districtId) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        districtId: newConstituency.districtId,
        sNo: parseInt(newConstituency.sNo) || 1, // Default to 1 if not provided (auto-assigned by backend)
        name: newConstituency.name.trim()
      };

      console.log('📤 Sending add constituency request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/add_constituency', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Add constituency response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Constituency created successfully!', 'Success');
        setNewConstituency({ name: '', districtId: '', sNo: '' });
        setAddStateId('');
        setAddDistricts([]);
        setAddConstituencyOpen(false);
        
        await fetchConstituencies();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to create constituency'}`, 'Create Constituency Error');
      }
    } catch (error) {
      console.error('Error adding constituency:', error);
      showErrorPopup('Error adding constituency. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Get district name by districtId
  const getDistrictName = (districtId) => {
    const district = districts.find(d => d.districtId === districtId);
    return district ? district.name : 'Unknown District';
  };

  // Handle individual constituency status toggle
  const handleConstituencyStatusToggle = async (constituencyId, currentStatus) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    try {
      const endpoint = currentStatus ? 'disable_constituency' : 'enable_constituency';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling constituency ${constituencyId} from ${currentStatus} to ${newStatus} using ${endpoint}`);

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          constituencyId
        }),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ Constituency ${newStatus ? 'enabled' : 'disabled'} successfully`);
        await fetchConstituencies();
      } else {
        console.error(`❌ Failed to ${newStatus ? 'enable' : 'disable'} constituency:`, result.message);
        showErrorPopup(`Error: ${result.message || `Failed to ${newStatus ? 'enable' : 'disable'} constituency`}`, 'Status Update Error');
      }
    } catch (error) {
      console.error('Error updating constituency status:', error);
      showErrorPopup('Error updating constituency status. Please try again.', 'Network Error');
    }
  };

  const handleMenuOpen = (event, constituency) => {
    setAnchorEl(event.currentTarget);
    setSelectedConstituency(constituency);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConstituency(null);
  };

  const handleEdit = () => {
    if (selectedConstituency) {
      setEditingConstituency({
        name: selectedConstituency.name,
        districtId: selectedConstituency.districtId,
        constituencyId: selectedConstituency.constituencyId,
        sNo: selectedConstituency.sNo || 1
      });
      // Fetch districts for the state of this constituency
      const constituencyStateId = selectedConstituency.stateId;
      if (constituencyStateId) {
        setEditStateId(constituencyStateId);
        fetchEditDistricts(constituencyStateId);
      }
      setEditConstituencyOpen(true);
    }
    handleMenuClose();
  };

  const handleEditConstituency = async () => {
    if (!editingConstituency.name.trim() || !editingConstituency.districtId) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      showErrorPopup('Session data not found. Please login again.', 'Session Error');
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        sessionId: sessionId,
        dashAdminId: dashAdminId,
        constituencyId: editingConstituency.constituencyId,
        sNo: editingConstituency.sNo,
        name: editingConstituency.name.trim()
      };

      console.log('📤 Sending edit constituency request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/edit_constituency', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Edit constituency response:', result);

      if (response.ok && result.status === true) {
        showSuccessPopup('Constituency updated successfully!', 'Success');
        setEditingConstituency({ name: '', districtId: '', constituencyId: '', sNo: 1 });
        setEditStateId('');
        setEditDistricts([]);
        setEditConstituencyOpen(false);
        
        await fetchConstituencies();
      } else {
        showErrorPopup(`Error: ${result.message || 'Failed to update constituency'}`, 'Update Constituency Error');
      }
    } catch (error) {
      console.error('Error editing constituency:', error);
      showErrorPopup('Error editing constituency. Please try again.', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStateChange = (event) => {
    const stateId = event.target.value;
    setAddStateId(stateId);
    setNewConstituency({...newConstituency, districtId: ''}); // Reset district when state changes
    if (stateId) {
      fetchAddDistricts(stateId);
    } else {
      setAddDistricts([]);
    }
  };

  const handleEditStateChange = (event) => {
    const stateId = event.target.value;
    setEditStateId(stateId);
    setEditingConstituency({...editingConstituency, districtId: ''}); // Reset district when state changes
    if (stateId) {
      fetchEditDistricts(stateId);
    } else {
      setEditDistricts([]);
    }
  };

  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedStateId(stateId);
    setSelectedDistrictId(''); // Reset district when state changes
    if (stateId) {
      fetchDistricts(stateId);
    } else {
      setDistricts([]);
    }
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrictId(event.target.value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Constituencies
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setAddConstituencyOpen(true)}
          sx={{ bgcolor: '#1976d2' }}
        >
          + Add Constituency
        </Button>
      </Box>
      
      {/* Pagination Controls - Top Row */}
      {totalPages > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${totalCount}`} 
            color="primary" 
            variant="outlined" 
            size="small"
            sx={{ fontSize: '0.75rem', height: '24px' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            Page {currentPage} of {totalPages}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.75rem',
                  minWidth: '28px',
                  height: '28px',
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Filter and Search Controls - Bottom Row */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label="Active"
          sx={{ color: '#1976d2', fontWeight: 500, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>State</Typography>
          <TextField
            select
            value={selectedStateId}
            onChange={handleStateChange}
            size="small"
            sx={{ 
              minWidth: { xs: 140, sm: 160, md: 180 },
              maxWidth: { xs: 180, sm: 200 },
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                height: '36px',
              },
            }}
            disabled={fetchingStates}
          >
            <MenuItem value="">
              <em>All States</em>
            </MenuItem>
            {states.map((state) => (
              <MenuItem key={state.stateId} value={state.stateId}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>District</Typography>
          <TextField
            select
            value={selectedDistrictId}
            onChange={handleDistrictChange}
            size="small"
            sx={{ 
              minWidth: { xs: 140, sm: 160, md: 180 },
              maxWidth: { xs: 180, sm: 200 },
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                height: '36px',
              },
            }}
            disabled={fetchingDistricts || !selectedStateId}
          >
            <MenuItem value="">
              <em>All Districts</em>
            </MenuItem>
            {districts.map((district) => (
              <MenuItem key={district.districtId} value={district.districtId}>
                {district.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              width: { xs: 140, sm: 160, md: 180 },
              maxWidth: { xs: 180, sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.875rem',
                height: '36px',
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* S.No column hidden - auto-assigned by backend */}
              {/* <TableCell>S.No</TableCell> */}
              <TableCell>Constituency</TableCell>
              <TableCell>District</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading constituencies...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : constituencies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No constituencies found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              constituencies.map((constituency, index) => (
                <TableRow key={constituency.constituencyId}>
                  {/* S.No column hidden - auto-assigned by backend */}
                  {/* <TableCell>{constituency.sNo || index + 1}</TableCell> */}
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{constituency.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {constituency.districtName || getDistrictName(constituency.districtId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={constituency.isActive === true}
                      onChange={() => handleConstituencyStatusToggle(constituency.constituencyId, constituency.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, constituency)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Constituency Dialog */}
      <Dialog 
        open={addConstituencyOpen} 
        onClose={() => setAddConstituencyOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Add Constituency</DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* S.No field hidden - auto-assigned by backend */}
            {/* <TextField
              label="S.No (Ranking)"
              type="number"
              fullWidth
              value={newConstituency.sNo}
              onChange={(e) => {
                const value = e.target.value;
                setNewConstituency({...newConstituency, sNo: value});
              }}
              placeholder="Enter ranking number..."
              inputProps={{ min: 1 }}
              required
              size="small"
            /> */}
            <TextField
              select
              label="State"
              fullWidth
              value={addStateId}
              onChange={handleAddStateChange}
              disabled={fetchingStates}
              required
              size="small"
            >
              {states.map((state) => (
                <MenuItem key={state.stateId} value={state.stateId}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="District"
              fullWidth
              value={newConstituency.districtId}
              onChange={(e) => setNewConstituency({...newConstituency, districtId: e.target.value})}
              disabled={fetchingAddDistricts || !addStateId}
              required
              size="small"
            >
              {addDistricts.map((district) => (
                <MenuItem key={district.districtId} value={district.districtId}>
                  {district.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Constituency Name"
              fullWidth
              value={newConstituency.name}
              onChange={(e) => setNewConstituency({...newConstituency, name: capitalizeFirstLetter(e.target.value)})}
              placeholder="Enter constituency name..."
              autoFocus
              required
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddConstituencyOpen(false);
            setNewConstituency({ name: '', districtId: '', sNo: '' });
            setAddStateId('');
            setAddDistricts([]);
          }}>Cancel</Button>
          <Button 
            onClick={handleAddConstituency} 
            variant="contained"
            disabled={loading || !newConstituency.name.trim() || !newConstituency.districtId}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Adding...' : 'Add Constituency'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Constituency Dialog */}
      <Dialog open={editConstituencyOpen} onClose={() => setEditConstituencyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Constituency</DialogTitle>
        <DialogContent>
          {/* S.No field hidden - auto-assigned by backend */}
          {/* <TextField
            label="S.No (Ranking)"
            type="number"
            fullWidth
            margin="normal"
            value={editingConstituency.sNo}
            onChange={(e) => setEditingConstituency({...editingConstituency, sNo: parseInt(e.target.value) || 1})}
            placeholder="Enter ranking number..."
            inputProps={{ min: 1 }}
            required
          /> */}
          <TextField
            select
            label="State"
            fullWidth
            margin="normal"
            value={editStateId}
            onChange={handleEditStateChange}
            disabled={fetchingStates}
            required
          >
            {states.map((state) => (
              <MenuItem key={state.stateId} value={state.stateId}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="District"
            fullWidth
            margin="normal"
            value={editingConstituency.districtId}
            onChange={(e) => setEditingConstituency({...editingConstituency, districtId: e.target.value})}
            disabled={fetchingEditDistricts || !editStateId}
            required
          >
            {editDistricts.map((district) => (
              <MenuItem key={district.districtId} value={district.districtId}>
                {district.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Constituency Name"
            fullWidth
            margin="normal"
            value={editingConstituency.name}
            onChange={(e) => setEditingConstituency({...editingConstituency, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter constituency name..."
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditConstituencyOpen(false);
            setEditingConstituency({ name: '', districtId: '', constituencyId: '', sNo: 1 });
            setEditStateId('');
            setEditDistricts([]);
          }}>Cancel</Button>
          <Button 
            onClick={handleEditConstituency} 
            variant="contained"
            disabled={loading || !editingConstituency.name.trim() || !editingConstituency.districtId}
            sx={{ bgcolor: '#1976d2' }}
          >
            {loading ? 'Updating...' : 'Update Constituency'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Export individual components for routing
export { DashboardHome, UsersView, CategoriesView, AdsView, StatesView, QualityManagersView, ApproversView, NewsView, DistrictsView, ConstituenciesView };

export default function SuperAdminDashboard({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get current route and map it to selected tab
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/news')) return 'News';
    if (path.includes('/addnewsarticle')) return 'Add News Article';
    if (path.includes('/categories')) return 'Categories';
    if (path.includes('/ads')) return 'Ads';
    if (path.includes('/states')) return 'States';
    if (path.includes('/districts')) return 'Districts';
    if (path.includes('/constituencies')) return 'Constituencies';
    if (path.includes('/users')) return 'Users';
    if (path.includes('/reporters')) return 'Reporters';
    if (path.includes('/qualitymanagers')) return 'Quality Managers';
    if (path.includes('/approvers')) return 'Approvers';
    // Default to News instead of Dashboard
    return 'News';
  };
  
  // Initialize selected tab from current route only
  const [selected, setSelected] = useState(() => getCurrentTab());
  const [reporterPopupOpen, setReporterPopupOpen] = useState(false);
  
  // Edit article state
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [editingArticleData, setEditingArticleData] = useState(null);
  
  // Custom popup state
  const [popupConfig, setPopupConfig] = useState({
    open: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false,
    onConfirm: null,
    onCancel: null
  });

  // Setup popup callback
  useEffect(() => {
    setPopupCallback((config) => {
      setPopupConfig({ ...config, open: true });
    });
  }, []);

  // No route preservation - keep it simple

  // Popup close handler
  const handlePopupClose = () => {
    setPopupConfig(prev => ({ ...prev, open: false }));
  };

  // Navigation handler
  const handleNavigationChange = (tabKey) => {
    setSelected(tabKey);
    
    // Navigate to the corresponding route
    switch (tabKey) {
      // Dashboard commented out - redirecting to news instead
      // case 'Dashboard':
      //   navigate('/superadmin/dashboard');
      //   break;
      case 'News':
        navigate('/superadmin/news');
        break;
      case 'Add News Article':
        navigate('/superadmin/addnewsarticle');
        break;
      case 'Categories':
        navigate('/superadmin/categories');
        break;
      case 'Ads':
        navigate('/superadmin/ads');
        break;
      case 'States':
        navigate('/superadmin/states');
        break;
      case 'Districts':
        navigate('/superadmin/districts');
        break;
      case 'Constituencies':
        navigate('/superadmin/constituencies');
        break;
      case 'Users':
        navigate('/superadmin/users');
        break;
      case 'Reporters':
        navigate('/superadmin/reporters');
        break;
      case 'Quality Managers':
        navigate('/superadmin/qualitymanagers');
        break;
      case 'Approvers':
        navigate('/superadmin/approvers');
        break;
      default:
        navigate('/superadmin/news');
    }
  };

  
  // Reporter state variables
  const [newReporter, setNewReporter] = useState({ name: '', phone: '', email: '', roleType: 3, emailotp: '' });
  const [reporterLoading, setReporterLoading] = useState(false);
  const [reporterEmailVerified, setReporterEmailVerified] = useState(false);
  const [verifyingReporterEmail, setVerifyingReporterEmail] = useState(false);
  const [reporterPhoneError, setReporterPhoneError] = useState('');
  const [reporterEmailError, setReporterEmailError] = useState('');




  
  // Reporter validation functions
  const validateReporterPhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    if (!phone.trim()) {
      setReporterPhoneError('');
    } else if (!phoneRegex.test(phone.trim())) {
      setReporterPhoneError('Phone number must be exactly 10 digits');
    } else {
      setReporterPhoneError('');
    }
  };

  const validateReporterEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!email.trim()) {
      setReporterEmailError('');
    } else if (!emailRegex.test(email.trim())) {
      setReporterEmailError('Email must be a valid Gmail address (e.g., example@gmail.com)');
    } else {
      setReporterEmailError('');
    }
  };
  
  // Reporter email verification
  const handleVerifyReporterEmail = async () => {
    if (!newReporter.email.trim()) {
      showWarningPopup('Please enter an email address first', 'Email Required');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newReporter.email.trim())) {
      showWarningPopup('Please enter a valid Gmail address (e.g., example@gmail.com)', 'Invalid Email Format');
      return;
    }

    setVerifyingReporterEmail(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/send_email_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newReporter.email }),
      });

      const data = await response.json();
      console.log('Email OTP response:', data);

      if (response.ok && data.status === true) {
        setReporterEmailVerified(true);
        showSuccessPopup('OTP sent successfully! Please check your email.', 'Email Verification');
      } else {
        showErrorPopup(data.message || 'Failed to send OTP. Please try again.', 'OTP Error');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      showErrorPopup('Error sending OTP. Please try again.', 'Network Error');
    } finally {
      setVerifyingReporterEmail(false);
    }
  };
  
  // Add reporter function
  const handleAddReporterSubmit = async () => {
    if (!newReporter.name.trim() || !newReporter.email.trim() || !newReporter.phone.trim()) {
      showWarningPopup('Please fill in all required fields', 'Required Fields Missing');
      return;
    }

    // Validate phone number - must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(newReporter.phone.trim())) {
      showWarningPopup('Please enter a valid 10-digit phone number', 'Invalid Phone Number');
      return;
    }

    // Validate email format - must end with @gmail.com
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!emailRegex.test(newReporter.email.trim())) {
      showWarningPopup('Please enter a valid Gmail address (e.g., example@gmail.com)', 'Invalid Email Format');
      return;
    }

    if (!reporterEmailVerified || !newReporter.emailotp.trim()) {
      showWarningPopup('Please verify your email with OTP first', 'Email Verification Required');
      return;
    }

    setReporterLoading(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/create_dashboard_admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newReporter,
          roleType: 3 // Ensure roleType is set for Reporter
        }),
      });

      const data = await response.json();
      console.log('Add reporter response:', data);

      if (response.ok) {
        console.log('Reporter added successfully:', data);
        setNewReporter({ name: '', phone: '', email: '', roleType: 3, emailotp: '' });
        setReporterEmailVerified(false);
        setReporterPopupOpen(false);
        
        // Show success message
        showSuccessPopup('Reporter added successfully!', 'Success');
      } else {
        showErrorPopup(data.message || 'Failed to add reporter. Please try again.', 'Add Reporter Error');
      }
    } catch (error) {
      console.error('Error adding reporter:', error);
      showErrorPopup('Error adding reporter. Please try again.', 'Network Error');
    } finally {
      setReporterLoading(false);
    }
  };

  // Navigation items for Super Admin
  const navigationItems = [
    // Dashboard commented out - redirecting to news instead
    // { key: 'Dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { key: 'News', label: 'News', icon: <ArticleIcon /> },
    { key: 'Add News Article', label: 'Add News Article', icon: <AddCircleOutlineIcon /> },
    { key: 'Categories', label: 'Categories', icon: <CategoryIcon /> },
    { key: 'Ads', label: 'Ads', icon: <CampaignIcon /> },
    { key: 'States', label: 'States', icon: <LocationOnIcon /> },
    { key: 'Districts', label: 'Districts', icon: <MapIcon /> },
    { key: 'Constituencies', label: 'Constituencies', icon: <MapIcon /> },
    { key: 'Users', label: 'Users', icon: <PersonIcon /> },
    { key: 'Reporters', label: 'Reporters', icon: <PeopleIcon /> },
    { key: 'Quality Managers', label: 'Quality Managers', icon: <SupervisorAccountIcon /> },
    { key: 'Approvers', label: 'Approvers', icon: <ApprovalIcon /> },
  ];

  console.log('🔍 Current state:', { selected, editingArticleId, editingArticleData });

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      selected={selected}
      onNavigationChange={handleNavigationChange}
      navigationItems={navigationItems}
      userRole="Super Admin"
      accessLevel="Full Access"
    >
      <SuperAdminRoutes
        onEditArticle={(articleId, articleData) => {
          console.log('🔄 Setting edit state:', { articleId, articleData });
          setEditingArticleId(articleId);
          setEditingArticleData(articleData);
          navigate(`/superadmin/editarticle/${articleId}`);
        }}
        onBackToNews={() => {
          console.log('🔙 Going back to news list');
          setEditingArticleId(null);
          setEditingArticleData(null);
          navigate('/superadmin/news');
        }}
        editingArticleId={editingArticleId}
        setEditingArticleId={setEditingArticleId}
        editingArticleData={editingArticleData}
        setEditingArticleData={setEditingArticleData}
        onAddReporter={() => setReporterPopupOpen(true)}
      />
      
      {/* Add Reporter Dialog */}
      <Dialog open={reporterPopupOpen} onClose={() => setReporterPopupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
          Add New Reporter
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newReporter.name}
            onChange={(e) => setNewReporter({...newReporter, name: capitalizeFirstLetter(e.target.value)})}
            placeholder="Enter reporter name..."
          />
          <TextField
            label="Phone"
            fullWidth
            margin="normal"
            value={newReporter.phone}
            onChange={(e) => {
              setNewReporter({...newReporter, phone: e.target.value});
              validateReporterPhone(e.target.value);
            }}
            onBlur={(e) => validateReporterPhone(e.target.value)}
            placeholder="Enter phone number..."
            error={!!reporterPhoneError}
            helperText={reporterPhoneError}
          />
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={newReporter.email}
            onChange={(e) => {
              setNewReporter({...newReporter, email: e.target.value});
              validateReporterEmail(e.target.value);
            }}
            onBlur={(e) => validateReporterEmail(e.target.value)}
            placeholder="Enter email address..."
            type="email"
            error={!!reporterEmailError}
            helperText={reporterEmailError}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleVerifyReporterEmail}
              disabled={verifyingReporterEmail || !newReporter.email.trim() || !!reporterEmailError}
              sx={{ borderColor: '#2196f3', color: '#2196f3' }}
            >
              {verifyingReporterEmail ? 'Sending...' : 'Verify Email'}
            </Button>
            {reporterEmailVerified && (
              <Typography color="success.main" sx={{ fontWeight: 500 }}>
                ✓ Email verified
              </Typography>
            )}
          </Box>
          {reporterEmailVerified && (
            <TextField
              label="Email OTP"
              fullWidth
              margin="normal"
              value={newReporter.emailotp}
              onChange={(e) => setNewReporter({...newReporter, emailotp: e.target.value})}
              placeholder="Enter email OTP..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReporterPopupOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddReporterSubmit} 
            variant="contained"
            disabled={reporterLoading || !newReporter.name.trim() || !newReporter.email.trim() || !newReporter.phone.trim() || !reporterEmailVerified || !newReporter.emailotp.trim()}
            sx={{ bgcolor: '#1976d2' }}
          >
            {reporterLoading ? 'Adding...' : 'Add Reporter'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Custom Popup */}
      <CustomPopup
        open={popupConfig.open}
        onClose={handlePopupClose}
        title={popupConfig.title}
        message={popupConfig.message}
        type={popupConfig.type}
        confirmText={popupConfig.confirmText}
        cancelText={popupConfig.cancelText}
        showCancel={popupConfig.showCancel}
        onConfirm={popupConfig.onConfirm}
        onCancel={popupConfig.onCancel}
      />
    </DashboardLayout>
  );
} 